//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'
import * as moment from 'moment'

//#endregion

//#region Angular Imports

import { StateService } from '@uirouter/angularjs'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'
import 'devextreme/ui/load_indicator'
import dxDataGrid, { dxDataGridOptions, dxDataGridColumn } from 'devextreme/ui/data_grid'
import { dxListItem } from 'devextreme/ui/list'

//#endregion

//#region Helper

import { IUserSession, ConvertHelper } from '../../helpers'
import { CommonHelper } from '../../helpers/common-helper'
import { ConfigHelper } from '../../helpers/config-helper'
import { EventsNames } from '../../helpers/events-names'
import { Md5 } from '../../helpers/md5-helper'
import { OperatorLayoutSettings } from '../../helpers/operator-layout-settings'
import { Permissions } from '../../helpers/permissions'
import { TemplateHelper } from '../../helpers/template-helper'
import { TypeHelper } from '../../helpers/type-helper'

//#endregion

//#region Model Imports

import { ColumnModel, EntityModel } from '../api/api.models'
import { Api, LocalDictionaryFactory } from '../api/api.module'
import { i18n } from './i18n/common.i18n'
import { OpView } from '../../models/core/op-view'
import { OpViewColumn } from '../../models/core/op-view-column'
import { Enums } from '../../models/common/enums'

//#endregion

//#region Service Imports

import { LoggerService } from './common.module'
import { DateBoxControlsService } from '../../services/controls'
import { GridService } from '../../services/grid'
import { DataTypeFormatService, DataTypeFormatter } from '../../services/data-type'

//#endregion

import { ButtonOptions, GridPositions as TreeListPositions } from './grid/grid.controller'
import { GridCustomButtonLocation, GridCustomButtonOptions } from './grid/grid.interfaces'
import { IMRBaseControlDirectiveController } from './base'
import { OperatorLayoutSetting } from '../../models/local'

interface Filter {
	where: string
	items?: {
		name: any
		where: string
		clearFilter?: () => void
		filterName?: string
		isCustom?: boolean
	}[]
	item?: {
		name: any
		where: string
		clearFilter?: () => void
		filterName?: string
		isCustom?: boolean
	}
}

export interface TableScope<T extends DevExpress.ui.WidgetOptions> extends ng.IScope {
	tableId: number

	buttonTooltipText: string
	columnChooserTooltipText: string
	tableOptions: T
	clearFilterButtonOptions: DevExpress.ui.dxButtonOptions
	filterBoxOptions: DevExpress.ui.dxTagBoxOptions
	buttonsListPopoverOptions: DevExpress.ui.dxPopoverOptions
	buttonsListOptions: DevExpress.ui.dxListOptions
	buttonsTooltipOptions: DevExpress.ui.dxTooltipOptions
	columnChooserTooltipOptions: DevExpress.ui.dxTooltipOptions
	positionPopoverOptions: DevExpress.ui.dxPopoverOptions
	positionListOptions: DevExpress.ui.dxListOptions
	selectionPopoverOptions: DevExpress.ui.dxPopoverOptions
	selectionListOptions: DevExpress.ui.dxListOptions
	popoverListButtonOptions: DevExpress.ui.dxButtonOptions
	chooseFilterPopoverOptions: DevExpress.ui.dxPopoverOptions
	chooseFilterTagBoxOptions: DevExpress.ui.dxTagBoxOptions

	filterListOptions: DevExpress.ui.dxListOptions
	filterPopoverOptions: DevExpress.ui.dxPopoverOptions
	filterListPopupOptions: DevExpress.ui.dxPopupOptions
	filterFormPopupOptions: DevExpress.ui.dxPopupOptions
	filterTextAreaOptions: DevExpress.ui.dxTextAreaOptions
	submitFilterButtonOptions: DevExpress.ui.dxButtonOptions
	resetFilterButtonOptions: DevExpress.ui.dxButtonOptions
	closeFilterFormButtonOptions: DevExpress.ui.dxButtonOptions
	backFilterButtonOptions: DevExpress.ui.dxButtonOptions
	pagerTooltipOptions: DevExpress.ui.dxTooltipOptions
	pagerTooltip: DevExpress.ui.dxTooltip

	headerButtons: any
	customHeaderButtons: any
	// directive methods
	checkColumns: () => boolean

	// directive fields
	show: boolean
	isFilterBoxVisible: boolean
	isButtonsListPopoverVisible: boolean

	// custom filter fields
	filterText: string
	filterTextLabel: string
	isSelectionPopoverVisible: boolean
	isCustomFilterPopoverVisible: boolean
	isFilterListPopupVisible: boolean
	isFilterFormPopupVisible: boolean
	isChooseFilterPopoverVisible: boolean
	submitFilterButtonDisabled: boolean
	clearFilterButtonDisabled: boolean

	isLargeGridHeader: boolean
	nothingSelected: boolean

	needButtonsListPopover: boolean
	needPositionPopover: boolean
	needSelectionPopover: boolean
	needCustomFilter: boolean
	needSaveColumnSettings: boolean
	needImport: boolean
	needChooseFilterPopover: boolean
	multiSelectEnabled: boolean

	showPageSizeSelectorText: boolean

	addButtonsListPopoverOptions: DevExpress.ui.dxPopoverOptions
	addButtonsListOptions: DevExpress.ui.dxListOptions
	isAddButtonsListPopoverVisible: boolean

	refreshFilterBox: boolean

	totalCount: number
}

export class TableController<T extends DevExpress.ui.Widget, K extends DevExpress.ui.WidgetOptions> extends IMRBaseControlDirectiveController {
	//#region Consts

	protected readonly LOG_STRING = '[SIMAX.Common.Table.Controller] - %s'
	private static readonly COLUMN_HAS_CUSTOM_CHOOSE_FILTER: string = 'COLUMN_HAS_CUSTOM_CHOOSE_FILTER'
	//#endregion

	table: T

	tableElement: JQuery
	tableDeferred: ng.IDeferred<T>
	isColumnChooserTooltipInitialized: boolean = false
	columnChooserTooltip: DevExpress.ui.dxTooltip

	dataSource: DataSource
	defaultColumns: ColumnModel[]

	buttons: ButtonOptions[]
	customButtons: GridCustomButtonOptions[]
	buttonsTooltip: DevExpress.ui.dxTooltip
	filterBox: DevExpress.ui.dxTagBox
	buttonsListPopover: DevExpress.ui.dxPopover
	buttonsListPopoverDeferred: ng.IDeferred<DevExpress.ui.dxPopover>
	buttonsList: DevExpress.ui.dxList
	positionPopover: DevExpress.ui.dxPopover
	positionPopoverDeferred: ng.IDeferred<DevExpress.ui.dxPopover>
	selectionPopover: DevExpress.ui.dxPopover
	selectionPopoverDeferred: ng.IDeferred<DevExpress.ui.dxPopover>
	filterList: DevExpress.ui.dxList
	filterPopover: DevExpress.ui.dxPopover
	filterFormPopup: DevExpress.ui.dxPopup

	chooseFilterTagBoxDeferred: ng.IDeferred<DevExpress.ui.dxTagBox>
	chooseFilterPopoverDeferred: ng.IDeferred<DevExpress.ui.dxPopover>

	filteredColumnName: string
	lastSelectedFilterColumnIndex: number
	customWhereClause: Filter

	sortData: any

	referenceTables: any

	hackForCustomFiltersInit = false

	clickTimer: any
	lastRowCLickedId: number
	onSelectionChangedTimeout: any

	lastVisibleColumns: any[]
	onChangeColumnsTimeout

	buttonsWidth: number

	hamburgerItems: any[]
	hamburgerItemsShort: any[]

	isMobile: boolean
	windowHeight: number
	windowWidth: number

	mapHeaderButton: DevExpress.ui.dxButton
	mapHeaderButtonDeferred: ng.IDeferred<DevExpress.ui.dxButton>
	previewHeaderButton: DevExpress.ui.dxButton
	previewHeaderButtonDeferred: ng.IDeferred<DevExpress.ui.dxButton>
	isTouchScreen: boolean = false
	selectedRows: number[] = []

	searchFilter: string

	needFormatDate: boolean = false
	static endDateBoxArray: any[] = []
	static startDateBoxArray: any[] = []
	colCount: number = 0

	addButton: DevExpress.ui.dxButton
	addButtonsListPopover: DevExpress.ui.dxPopover
	addButtonsList: DevExpress.ui.dxList
	changeDateToUTC: boolean = false
	isFilteredLocally: boolean = false
	filterTextArea: DevExpress.ui.dxTextArea

	canRefreshFilters: boolean = true
	customChooseLastTarget: string = null

	lastFiltersMD5: string

	view: OpView = null
	primaryColumn: OpViewColumn = null

	treeListCustomFilterColumnName: string = null
	isTreeList: boolean = false

	public isButtonListClickedOff = false

	/**
	 * Used to indicate whether grid filters should be reset when applying contenxt action filtering (filtering by group of ids)
	 */
	protected shouldResetContextActionFilter = true

	/**
	 * TODO
	 */
	protected shouldSkipIdFilterReset = false
	/**
	 * TODO
	 */
	protected dataTypeFormatter: DataTypeFormatter

	constructor(
		protected options,
		protected $scope: TableScope<K>,
		protected $timeout: ng.ITimeoutService,
		protected $state: StateService,
		protected logger: LoggerService,
		protected $q: ng.IQService,
		protected localDictionary: LocalDictionaryFactory,
		protected api: Api,
		protected $compile: ng.ICompileService,
		protected userSession: IUserSession,
		protected dataTypeFormatService: DataTypeFormatService,
		protected dateBoxControlsService: DateBoxControlsService
	) {
		super()

		angular.element('body').on('touchstart', () => {
			this.isTouchScreen = true
		})

		$scope.tableId = new Date().getTime()

		$scope.$on(EventsNames.HIDE_BUTTONS_LIST_POPOVER, (event: ng.IAngularEvent, ...args: any[]) => {
			if (this.buttonsListPopover) this.buttonsListPopover.hide()
		})

		$scope.$on('moduleChanging', () => {
			this.$scope.show = false
			if (this.$scope.pagerTooltip) this.$scope.pagerTooltip.hide()
		})

		$scope.$on(EventsNames.SHOW_GRID + this.$scope.tableId, () => {
			this.$scope.show = true
		})

		$scope.$on(EventsNames.HIDE_GRID + this.$scope.tableId, () => {
			this.$scope.show = false
			if (this.$scope.pagerTooltip) this.$scope.pagerTooltip.hide()
		})

		$scope.$on('stopModuleChanging', () => {
			this.$scope.show = true
		})

		$scope.$on(EventsNames.SERVER_EXCEPTION_OCCURED, () => {
			this.$scope.$root.$broadcast('disableLoadingIndicator')
			this.$scope.show = true
			if (this.table) {
				; (<any>this.table).endCustomLoading()
			}
		})
	}

	protected getActiveView(): JQueryPromise<OpView> {
		const idReferenceType: number = this.$scope.options.referenceId

		if (idReferenceType != null) {
			return this.userSession
				.getDataProvider()
				.View.getActiveView(idReferenceType)
				.then((view: OpView) => {
					if (view == null) {
						console.info(this.LOG_STRING, 'Unable to find the view for the given referenceId, proceeding without it.')
					}

					return view
				})
				.fail((error: any) => {
					console.warn(this.LOG_STRING, 'Unable to load the view for the given referenceId, proceeding without it.', error)
				})
		}

		console.info(this.LOG_STRING, 'No reference type given, proceeding without a view.')

		return null
	}

	protected callBaseClassConstructor() {
		this.$scope.checkColumns = () => {
			var columnsCount = 0
			if (this.table) columnsCount = (<any>this.table).columnCount()
			return columnsCount === 0 && this.$scope.show
		}

		this.$scope.isButtonsListPopoverVisible = false
		this.$scope.isAddButtonsListPopoverVisible = false
		this.$scope.isLargeGridHeader = true
		this.$scope.nothingSelected = true

		this.tableDeferred = this.$q.defer<T>()
		this.buttonsListPopoverDeferred = this.$q.defer<DevExpress.ui.dxPopover>()
		this.positionPopoverDeferred = this.$q.defer<DevExpress.ui.dxPopover>()
		this.selectionPopoverDeferred = this.$q.defer<DevExpress.ui.dxPopover>()
		this.chooseFilterPopoverDeferred = this.$q.defer<DevExpress.ui.dxPopover>()
		this.chooseFilterTagBoxDeferred = this.$q.defer<DevExpress.ui.dxTagBox>()

		this.$scope.clearFilterButtonOptions = {
			icon: 'clear',
			onClick: (e) => {
				this.clearCustomFilter()
				if (this.table) {
					var columns = []
					for (var i = 0; i < (<any>this.table).columnCount(); i++) {
						var column: DevExpress.ui.dxDataGridColumn = (<any>this.table).columnOption(i)
						if (column.selectedFilterOperation) column.selectedFilterOperation = undefined
						column.filterValue = undefined
						column.filterValues = undefined
						columns.push(column)
					}
					this.$scope.$emit(EventsNames.FILTERS_CLEARED)
						; (<any>this.table).clearFilter()
				}
				this.$scope.$emit(EventsNames.RESET_MAP_STYLE)

				if (this.filterBox) {
					this.shouldSkipIdFilterReset = true
					this.filterBox.reset()
					this.shouldSkipIdFilterReset = false
				}
			},
		}

		var positionList = [TreeListPositions.left, TreeListPositions.right, TreeListPositions.top, TreeListPositions.bottom, TreeListPositions.hide]
		this.$scope.positionListOptions = {
			items: positionList.map((key) => i18n('Grid', key)),
			onItemClick: (e) => {
				this.hidePosisionPopover()

				this.hideButtonPopover()
				if (positionList[e.itemIndex] == TreeListPositions.hide) {
					if (this.mapHeaderButton) this.mapHeaderButton.option('hint', i18n('Grid', 'ShowMap'))
					if (this.previewHeaderButton) this.previewHeaderButton.option('hint', i18n('Grid', 'ShowPreview'))
				}
				if (this.options && this.options.onUpdateGridsterPosition) this.options.onUpdateGridsterPosition(positionList[e.itemIndex])
			},
		}
		if ((<any>this.$scope.tableOptions).columns) {
			const items: DevExpress.ui.dxDataGridColumn[] = (<any>this.$scope.tableOptions).columns.filter((c) => !c['hideInCustomFilter'] && c.dataType != 'binary' && c.dataType != 'date')

			this.$scope.filterListOptions = {
				items: items,
				displayExpr: 'caption',
				onInitialized: (e) => {
					this.filterList = e.component
					if (!this.lastSelectedFilterColumnIndex) {
						this.selectFirstColumnForCustomFilter()
					} else this.filterList.selectItem(this.lastSelectedFilterColumnIndex)
				},
				selectionMode: 'single',
				onItemClick: (e: any) => {
					this.$scope.filterTextLabel = e.itemData.caption

					if (!this.$scope.isLargeGridHeader) {
						this.$scope.isFilterListPopupVisible = false
						this.$scope.isFilterFormPopupVisible = true

						if (this.filterFormPopup) {
							this.filterFormPopup.show()
						}
					}

					this.filteredColumnName = e.itemData.dataField
					this.setCustomFilterButtonsState()
					this.lastSelectedFilterColumnIndex = e.itemIndex
				},
			}
		}
		// custom popovers
		var commonPopoverCSS = {
			padding: 0,
			'text-align': 'center',
			'border-radius': 'inherit',
		}

		// buttons list popover
		this.$scope.buttonsListPopoverOptions = {
			target: '#popoverListButton' + this.$scope.tableId,
			width: 250,
			visible: false,
			bindingOptions: {
				visible: 'isButtonListPopoverVisible',
			},
			onInitialized: (e) => {
				this.buttonsListPopover = e.component
				this.buttonsListPopoverDeferred.resolve(e.component)
			},
			onShowing: (e) => {
				e.component.content().css(commonPopoverCSS)
			},
			onHidden: () => {
				this.hideButtonPopover()
			},
		}

		var buttonsPermissionDefer = this.$q.defer()
		this.$q
			.all(
				this.buttons.map((button) => {
					var defer = this.$q.defer<boolean>()
					if (button.permissions) {
						Permissions.hasPermissions(button.permissions.replace('[', '').replace(']', '').replace(new RegExp("'", 'g'), '').split(',')).then((hasPermission) => {
							button['active'] = hasPermission
							defer.resolve(hasPermission)
						})
					} else {
						button['active'] = true
						defer.resolve(true)
					}
					return defer.promise
				})
			)
			.then(() => {
				this.hamburgerItems = this.buttons.filter((button) => button['active'])
				this.hamburgerItemsShort = this.hamburgerItems.filter(
					(button) =>
						[
							'multiSelect',
							'addButton',
							'editButton',
							'selectionButton',
							'showPreviewButton',
							'showDetailsButton',
							'actionFilters',
							'showMapButton',
							'refreshButton',
							'depositoryContentEditButton',
							'groupingLocationAssignLocationButton',
							'deleteRowsButton',
							'problemConfirmationButton',
							'planDeliveryButton',
							'toggleDetailsRows',
						].indexOf(button['name']) < 0
				)

				const dropdownCustomButtons: GridCustomButtonOptions[] = _.filter(this.customButtons, (customButton: GridCustomButtonOptions) => customButton.location === GridCustomButtonLocation.Dropdown)

				this.hamburgerItems = [...this.hamburgerItems, ...(this.customButtons || [])]
				this.hamburgerItemsShort = [...dropdownCustomButtons, ...this.hamburgerItemsShort]

				buttonsPermissionDefer.resolve()
			})

		this.$scope.buttonsListOptions = {
			items: [],
			itemTemplate: (...args) => {
				const gridButtonOptions: ButtonOptions | GridCustomButtonOptions = args[0]
				const container: JQuery = args[2]
				const itemIndex: number = args[1]
				const buttonOptions: DevExpress.ui.dxButtonOptions = TypeHelper.clone(gridButtonOptions.options, true)
				let elString: string = null
				let fieldInScope: string = null

				if (buttonOptions != null) {
					let buttonTitle: string = buttonOptions.hint

					if (buttonOptions['getDynamicTitle']) {
						buttonTitle = buttonOptions['getDynamicTitle']()
					}

					if (buttonOptions.template != null) {
						buttonOptions.template =
							"<div class='row grid__dropdown-list-button'><div class='icon-row'>" +
							buttonOptions.template +
							"</div><span class='grid__dropdown-list-button-span'>" +
							' ' +
							buttonTitle +
							'</span></div>'
					}
				}

				if ('location' in gridButtonOptions) {
					fieldInScope = itemIndex.toString() + this.$scope.tableId.toString() + 'small'
					elString = '<div dx-button=\'customHeaderButtons["' + fieldInScope + '"]\'>'

					this.$scope.customHeaderButtons[fieldInScope] = buttonOptions
				} else {
					const oldButtonOptions: ButtonOptions = gridButtonOptions as ButtonOptions

					fieldInScope = oldButtonOptions.name + this.$scope.tableId + 'small'
					elString =
						'<div dx-button=\'headerButtons["' +
						fieldInScope +
						"\"]' id='" +
						oldButtonOptions.name +
						this.$scope.tableId +
						"' " +
						(oldButtonOptions.permissions ? 'ng-if="$root.hasPermissions(' + args[0].permissions + ')"' : '') +
						'>'

					this.$scope.headerButtons[fieldInScope] = buttonOptions
				}

				const button: JQuery = angular.element(elString).css({
					'border-radius': 0,
					'text-overflow': 'ellipsis',
					border: 0,
					margin: 0,
					padding: '5px 10px',
					width: '100%',
				})

				const buttonWidth: number = container.get(0).clientWidth

				if (buttonWidth != null && this.buttonsTooltip) {
					container.mouseenter((e) => {
						const fontSize: string = container.css('font-size')
						const fontFamily: string = container.css('font-family')
						const text: string = e.target.textContent
						const iconWidth: number = parseInt($('.icon-row').css('width'))
						if (TemplateHelper.getTextWidth(text, fontSize, fontFamily) + iconWidth > buttonWidth) {
							this.$scope.buttonTooltipText = e.target.textContent
							this.buttonsTooltip.show(e.target)
						} else {
							this.buttonsTooltip.hide()
						}
					}),
						container.mouseleave(() => {
							this.buttonsTooltip.hide()
						})
				}
				container.append(this.$compile(button)(this.$scope))
				container.css({
					padding: 0,
					margin: 0,
				})
			},
			onInitialized: (e) => {
				this.buttonsList = e.component
			},
		}

		this.$scope.addButtonsListPopoverOptions = {
			target: '#addButton' + this.$scope.tableId,
			width: 250,
			visible: false,
			bindingOptions: {
				visible: 'isAddButtonListPopoverVisible',
			},
			onInitialized: (e) => {
				this.addButtonsListPopover = e.component
				//this.buttonsListPopoverDeferred.resolve(e.component)
			},
			onShowing: (e) => {
				e.component.content().css(commonPopoverCSS)
			},
			onHidden: () => {
				this.hideAddButtonPopover()
			},
		}

		this.$scope.addButtonsListOptions = {
			items: [],
			itemTemplate: (...args) => {
				var fieldInScope = args[0].name + this.$scope.tableId + 'small'
				var el: JQuery = args[2]
				var button = angular.element(
					'<div dx-button=\'headerButtons["' +
					fieldInScope +
					"\"]' id='" +
					args[0].name +
					this.$scope.tableId +
					"' " +
					" style='width: 100%; padding: 5px 10px; border: 0; margin: 0; border-radius: 0'>"
				)
				var buttonOptions = angular.copy(args[0].options)
				buttonOptions.template = "<div class='row'><span>" + ' ' + args[0].options.hint + '</span></div>'

				this.$scope.headerButtons[fieldInScope] = buttonOptions

				el.append(this.$compile(button)(this.$scope))
				el.css({
					padding: 0,
					margin: 0,
				})
			},
			onInitialized: (e) => {
				this.addButtonsList = e.component
			},
		}

		this.$scope.selectionPopoverOptions = {
			target: '#selectionButton' + this.$scope.tableId,
			width: 100,
			bindingOptions: {
				visible: 'isSelectionPopoverVisible',
			},
			onShowing: (e) => {
				e.component.content().css(commonPopoverCSS)
			},
			onHiding: (e) => {
				this.$scope.isSelectionPopoverVisible = false
			},
			onInitialized: (e) => {
				this.selectionPopover = e.component
				this.selectionPopoverDeferred.resolve(e.component)
			},
		}

		var selectionOptions = {
			all: i18n('Grid', 'SelectionAll'),
			none: i18n('Grid', 'SelectionNone'),
			invert: i18n('Grid', 'SelectionInvert'),
		}

		var invert = (keys: any[], selectedKeys: any[]) => {
			var deselectKeys = selectedKeys
			var selectKeys = []
			var table = <any>this.table

			keys.forEach((key) => {
				if (selectedKeys.indexOf(key) < 0) selectKeys.push(key)
			})
			table.deselectRows(deselectKeys)
			table.selectRows(selectKeys, true)
		}

		this.$scope.selectionListOptions = {
			items: [selectionOptions.all, selectionOptions.invert, selectionOptions.none],
			onItemClick: (e) => {
				this.hideButtonPopover()
				this.$scope.isSelectionPopoverVisible = false
				this.table.beginUpdate()
				var table = <any>this.table
				switch (e.itemData) {
					case selectionOptions.all:
						var ds = this.table.option('dataSource')
						var items = ds.items(),
							keys
						if (ds.store()._key) keys = items.map((i) => i.id)
						else keys = items
						table.selectRows(keys, true)
						//table.selectAll()
						break
					case selectionOptions.none:
						table.clearSelection()
						break
					case selectionOptions.invert:
						var deselectKeys = []
						var selectKeys = []
						var ds = this.table.option('dataSource')
						var items = ds.items(),
							keys
						if (ds.store()._key) keys = items.map((i) => i.id)
						else keys = items
						var selectedRowKeys = table.getSelectedRowKeys()
						if (selectedRowKeys.then) {
							selectedRowKeys.then((selectedKeys) => {
								invert(keys, selectedKeys)
							})
						} else {
							invert(keys, selectedRowKeys)
						}
						break
				}
				this.table.endUpdate()
				this.updatePagerInfoText()
			},
		}

		this.$scope.positionPopoverOptions = {
			width: 160,
			visible: false,
			onShowing: (e) => {
				e.component.content().css(commonPopoverCSS)
			},
			onInitialized: (e) => {
				this.positionPopover = e.component
				this.positionPopoverDeferred.resolve(e.component)
			},
		}

		this.$scope.filterPopoverOptions = {
			height: 250,
			width: 700,
			target: '#filterButton' + this.$scope.tableId,
			onInitialized: (e) => {
				this.filterPopover = e.component
			},
			onShowing: (e) => {
				e.component.content().css({
					padding: 0,
					'border-radius': 'inherit',
				})
				if (this.$scope.$root['rtlState']) {
					e.component.content().css({
						display: 'flex',
					})
				}

				this.lastSelectedFilterColumnIndex = OperatorLayoutSettings.getSetting(CommonHelper.LAST_SELECTED_ROW + '_' + this.$scope.options.name, 0)
				this.filterList.selectItem(this.lastSelectedFilterColumnIndex)

				this.filteredColumnName = OperatorLayoutSettings.getSetting(CommonHelper.FILTERED_COLUMN + '_' + this.$scope.options.name, null)
				this.$scope.filterText = OperatorLayoutSettings.getSetting(CommonHelper.FILTER_TEXT + '_' + this.$scope.options.name, null)
				this.$scope.filterTextLabel = OperatorLayoutSettings.getSetting(CommonHelper.FILTER_LABEL + '_' + this.options.name, null)

				if (this.$scope.filterTextLabel == null) {
					if (this.lastSelectedFilterColumnIndex < (<dxDataGridOptions>this.$scope.tableOptions).columns.length) {
						this.$scope.filterTextLabel = ((<dxDataGridOptions>this.$scope.tableOptions).columns[this.lastSelectedFilterColumnIndex] as dxDataGridColumn).caption
						this.filteredColumnName = ((<dxDataGridOptions>this.$scope.tableOptions).columns[this.lastSelectedFilterColumnIndex] as dxDataGridColumn).dataField
					}
				}

				if (this.filterTextArea != null) {
					this.filterTextArea.option({ value: this.$scope.filterText })
				}

				this.setCustomFilterButtonsState()
			},
			onHidden: (e) => {
				if (!this.isButtonListClickedOff) {
					this.buttonsListPopover.hide()
				} else this.isButtonListClickedOff = false
				this.$scope.isCustomFilterPopoverVisible = false
			},
		}

		this.$scope.filterListPopupOptions = {
			height: 300,
			width: 300,
			bindingOptions: {
				visible: 'isFilterListPopupVisible',
			},
			onHidden: () => {
				this.$scope.isFilterListPopupVisible = false
			},
			onShowing: () => {
				if (this.lastSelectedFilterColumnIndex) {
					this.filterList.selectItem(this.lastSelectedFilterColumnIndex)
				}
			},
		}

		this.$scope.filterFormPopupOptions = {
			maxHeight: 330,
			width: 300,
			bindingOptions: {
				visible: 'isFilterFormPopupVisible',
			},
			onHidden: () => {
				this.$scope.isFilterFormPopupVisible = false
			},
			onInitialized: (e) => {
				this.filterFormPopup = e.component
			},
		}

		this.$scope.filterTextAreaOptions = {
			height: 120,
			bindingOptions: {
				value: 'filterText',
			},
			onInitialized: (e) => {
				this.filterTextArea = e.component
			},
			onInput: (e) => {
				this.$scope.filterText = (e.element.find('textarea')[0] as HTMLTextAreaElement).value
				this.setCustomFilterButtonsState()
			},
		}

		this.$scope.resetFilterButtonOptions = {
			text: i18n('Popup', 'Clear'),
			onClick: () => {
				this.clearCustomFilter()
				if (!this.$scope.isLargeGridHeader) {
					this.filterFormPopup.hide()
					this.$scope.isFilterFormPopupVisible = false
					this.$scope.isFilterListPopupVisible = true
				}
			},
			disabled: this.$scope.clearFilterButtonDisabled,
			bindingOptions: {
				disabled: 'clearFilterButtonDisabled',
			},
		}

		this.$scope.backFilterButtonOptions = {
			text: i18n('Popup', 'Back'),
			onClick: () => {
				this.$scope.isFilterFormPopupVisible = false
				this.filterFormPopup.hide()
				this.$scope.isFilterListPopupVisible = true
			},
		}

		this.$scope.closeFilterFormButtonOptions = {
			template: TemplateHelper.getIcon(ConfigHelper.Icon.CLOSE, this.userSession),
			onClick: () => {
				this.filterFormPopup.hide()
				this.$scope.isFilterFormPopupVisible = false
			},
		}

		this.$scope.submitFilterButtonOptions = {
			text: i18n('Popup', 'Filter'),
			onClick: () => {
				this.customFilterColumns()
				try {
					; (<any>this.table).refresh()
				} catch (e) {
					this.problemInTable(e)
				}
				if (this.$scope.isLargeGridHeader) {
					this.filterPopover.hide()
				} else {
					this.$scope.isFilterFormPopupVisible = false
					this.filterFormPopup.hide()
				}

				OperatorLayoutSettings.setSetting(CommonHelper.FILTER_TEXT + '_' + this.options.name, this.$scope.filterText)
				OperatorLayoutSettings.setSetting(CommonHelper.FILTERED_COLUMN + '_' + this.$scope.options.name, this.filteredColumnName)
				OperatorLayoutSettings.setSetting(CommonHelper.LAST_SELECTED_ROW + '_' + this.options.name, this.lastSelectedFilterColumnIndex)
				OperatorLayoutSettings.setSetting(CommonHelper.FILTER_LABEL + '_' + this.options.name, this.$scope.filterTextLabel)
			},
			disabled: this.$scope.submitFilterButtonDisabled,
			bindingOptions: {
				disabled: 'submitFilterButtonDisabled',
			},
		}

		this.$scope.popoverListButtonOptions = {
			onClick: () => {
				this.$scope.needButtonsListPopover = true
				this.$timeout(() => {
					this.buttonsListPopoverDeferred.promise.then(() => {
						this.$scope.isButtonsListPopoverVisible = !this.$scope.isButtonsListPopoverVisible

						if (this.$scope.isButtonsListPopoverVisible) {
							this.buttonsListPopover.show()
							this.colorFixingButton()
						} else {
							this.buttonsListPopover.hide()
						}
					})
				})
			},
			template: TemplateHelper.getIcon(ConfigHelper.Icon.SHOW_OPTIONS, this.userSession),
		}

		this.$scope.chooseFilterPopoverOptions = {
			width: 400,
			bindingOptions: {
				visible: 'isChooseFilterPopoverVisible',
			},
			visible: false,
			onHiding: (e) => {
				this.$scope.isChooseFilterPopoverVisible = false
			},
			onInitialized: (e) => {
				this.chooseFilterPopoverDeferred.resolve(e.component)
			},
		}

		this.$scope.chooseFilterTagBoxOptions = {
			displayExpr: (e) => {
				return e ? e.caption : null
			},
			onInitialized: (e) => {
				this.chooseFilterTagBoxDeferred.resolve(e.component)
			},
			displayValue: 'caption',
			visible: true,
			searchEnabled: true,
			noDataText: i18n('Common', 'NoDataTypesText'),
			placeholder: i18n('Common', 'SelectPlaceholder'),
		}

		this.$scope.buttonsTooltipOptions = <DevExpress.ui.dxTooltipOptions>{
			position: 'left',
			animation: false,
			onInitialized: (e) => {
				this.buttonsTooltip = e.component
			},
		}

		this.$scope.columnChooserTooltipOptions = <DevExpress.ui.dxTooltipOptions>{
			position: 'left',
			animation: false,
			hideEvent: 'dxhoverend',
			onInitialized: (e) => {
				this.columnChooserTooltip = e.component
			},
		}

		this.$scope.pagerTooltipOptions = {
			target: '#gridContainer' + this.$scope.tableId + ' .dx-page-sizes',
			showEvent: 'dxhoverstart',
			hideEvent: 'dxhoverend',
			position: 'right',
			onInitialized: (e) => {
				this.$scope.pagerTooltip = e.component
			},
			onShowing: () => {
				this.$timeout(() => {
					if (this.$scope.pagerTooltip != null) {
						this.$scope.pagerTooltip.hide()
					}
				}, 4000)
			},
		}

		this.$scope.$on(EventsNames.WINDOW_RESIZED, () => {
			if (this.$scope.pagerTooltip) this.$scope.pagerTooltip.hide()
		})

		// hack for stashed grid button list

		this.$scope.$watch('isLargeGridHeader', (val: boolean) => {
			if (!this.buttonsList) {
				buttonsPermissionDefer.promise.then(() => {
					this.$scope.buttonsListOptions.items = val ? this.hamburgerItemsShort : this.hamburgerItems
				})
				return
			}
			this.buttonsList.option('items', val ? this.hamburgerItemsShort : this.hamburgerItems)
			this.buttonsList.repaint()
		})

		// hack for column chooser
		this.$scope.$watch(
			() => {
				if (this.table) return this.table['_views'].columnChooserView && this.table['_views'].columnChooserView._columnChooserList
				return null
			},
			(result) => {
				if (result) {
					this.table['_views'].columnChooserView._columnChooserList._setFocusedItem = () => {
						// problem with column chooser
					}
				}
			}
		)
		// ---

		this.$scope.$on('secondrowclosed', () => {
			if (this.mapHeaderButton) this.mapHeaderButton.option('hint', i18n('Grid', 'ShowMap'))
			if (this.previewHeaderButton) this.previewHeaderButton.option('hint', i18n('Grid', 'ShowPreview'))
		})

		var headerWatch: () => void = null
		this.$scope.$on('customButtonsGridAdded', (event, header: JQuery) => {
			if (headerWatch) headerWatch()
			headerWatch = this.$scope.$watch(
				() => {
					if (this.table) {
						var toolbar = angular.element(this.table.element()).find('.dx-toolbar-items-container')
						var toolbarWidth = toolbar.width()
						var buttons = toolbar.find('.dx-toolbar-button')
						if (!this.buttonsWidth && this.$scope.isLargeGridHeader)
							this.buttonsWidth = _.reduce(
								buttons,
								(nemo: number, button) => {
									return nemo + angular.element(button).outerWidth()
								},
								0
							)
						return toolbarWidth - this.buttonsWidth
					}
					return 0
				},
				(width) => {
					this.checkButtonsWidth(width)
				}
			)
		})

		this.$scope.$on(EventsNames.LOAD_COLUMN_SETTINGS + this.$scope.tableId, (event: ng.IAngularEvent, ...args: any[]) => {
			if (this.table) {
				var storageColumnSetting: any = OperatorLayoutSettings.getSetting('SAVED_TABLE_' + this.options.name, null)
				var columns = []
				if (storageColumnSetting.columns !== false) {
					for (var i = 0; i < (<any>this.table).columnCount(); i++) {
						columns.push((<any>this.table).columnOption(i))
					}
					var loadColumns: any[] = storageColumnSetting.columns

					if (loadColumns) {
						columns.forEach((column) => {
							loadColumns.forEach((lColumn) => {
								if (column.dataField == lColumn.dataField) {
									column.visible = lColumn.visible
									column.visibleIndex = lColumn.visibleIndex
									column.width = lColumn.width
									column.sortOrder = lColumn.sortOrder
									column.sortIndex = lColumn.sortIndex
									column.filterValue = lColumn.filterValue
								}
							})
						})
					}
				} else {
					columns = this.getDxColumns(this.options.columnsDictionary, this.options.defaultColumnsDictionary)
				}

				this.table.option({
					columns: columns,
				})
			}
		})

		this.$scope.$on(EventsNames.CHANGED_ALLOWED_DISTRIBUTORS, (event: ng.IAngularEvent) => {
			if (this.table) {
				try {
					; (<any>this.table).refresh()
				} catch (e) {
					this.problemInTable(e)
				}
			}
		})

		this.$scope.$on(EventsNames.UPDATE_GRID_BUTTONS + this.$scope.tableId, (event: ng.IAngularEvent, ...args) => {
			this.updateButtonsHints(args[0], args[1])
		})

		this.$scope.$on(EventsNames.UPDATE_GRID_COLUMNS + this.$scope.tableId, (event: ng.IAngularEvent, ...args) => {
			; (<any>this.$scope.tableOptions).columns = args[0]
		})

		this.$scope.$on(EventsNames.UPDATE_GRID_DATASOURCE + this.$scope.tableId, (event: ng.IAngularEvent, ...args) => {
			if (args[0])
				this.tableDeferred.promise.then((table) => {
					table.option('dataSource', args[0])
					if (args[0].store && args[0].store()) {
						args[0]
							.store()
							.totalCount()
							.then((totalCount) => {
								this.$scope.totalCount = totalCount
							})
					} else {
						this.$scope.totalCount = 0
					}
					try {
						table.repaint()
					} catch (e) {
						this.problemInTable(e)
					}
				})
		})

		this.$scope.$on('$destroy', () => {
			this.cleanUp()
		})
	}

	protected cleanUp() {
		this.table = null
	}

	protected problemInTable(e) {
		console.log(e)
		console.error('Problem in table. Please contact with admin.')
	}

	private getColumnCaption(columns: any[], columnName: any, item: any) {
		const column = columns.filter((col) => {
			return col.dataField && col.dataField.toUpperCase() == columnName.toUpperCase()
		})

		if (column.length > 0) {
			if (item.isCustom) {
				return column[0].caption + ' [CUSTOM_ICON=FILTER]'
			}

			return column[0].caption
		} else {
			if (this.options.referenceId) {
				if (this.view != null) {
					const viewColumn: OpViewColumn = this.view.getViewColumnByName(columnName)

					if (viewColumn != null) {
						if (item['isCustom']) {
							return viewColumn.caption + ' [CUSTOM_ICON=FILTER]'
						}

						return viewColumn.caption
					}
				}
			}

			const caption: string = this.localDictionary.getCaptionByIdDataType(Number(columnName))

			if (caption) {
				if (item['isCustom']) {
					return caption + ' [CUSTOM_ICON=FILTER]'
				}

				return caption
			}
		}
	}

	protected refreshFilters() {
		this.$timeout(() => {
			if (this.table) {
				if (this.filterBox) {
					var table = <any>this.table
					var filterValue = []
					var columns = (<any>this.$scope.tableOptions).columns

					const combinedFilter = table.getCombinedFilter(true)

					const newFilters = {
						combinedFilter: combinedFilter,
						customWhereClause: this.customWhereClause ? this.customWhereClause.item : null,
						ids: this.$state.params['ids'],
						additionalFilters: this.options.additionalFilters,
					}

					const newFiltersMd5: string = Md5.init(newFilters)

					if (this.lastFiltersMD5 != newFiltersMd5) {
						this.lastFiltersMD5 = newFiltersMd5
						this.needFormatDate = true
						const whereItems: Filter['items'] = this.getWhereClause(combinedFilter).items
						this.needFormatDate = false

						// column filters or search if we have one column in grid or custom from popover if localy
						var items = whereItems.filter((item) => !item['isSearch'])
						items.forEach((item) => {
							var filterItem = {
								filter: item.where,
								clearFilter: item.clearFilter,
								filterName: item.filterName,
							}
							if (columns) {
								if (angular.isArray(item.name)) {
									item.name.forEach((f) => {
										var columnCaption = this.getColumnCaption(columns, f.name, item)
										filterItem.filter = filterItem.filter.split('[' + f.name + ']').join('[' + columnCaption + ']')
									})
								} else {
									var columnCaption = this.getColumnCaption(columns, item.name, item)
									if (!columnCaption) columnCaption = i18n('Grid', item.name)

									filterItem.filter = filterItem.filter.split('[' + item.name + ']').join('[' + columnCaption + ']')
								}
							}
							// distinct
							if (filterValue.filter((f) => f.filter == filterItem.filter).length > 0) {
								return
							} else {
								filterValue.push(filterItem)
							}
						})
						// one column in grid = we have to clean search when filter is removing
						if (filterValue.length == 1 && table.getVisibleColumns().length == 1) {
							var self = this
							var lastClear = filterValue[0].clearFilter
							filterValue[0].clearFilter = () => {
								lastClear()
								self.table['clearFilter']('search')
							}
						}

						// search filters
						var searchItems = whereItems
							.filter((item) => item['isSearch'])
							.map((item) => {
								if (columns) {
									var column = columns.filter((col) => {
										return col.dataField.toUpperCase() == item.name.toUpperCase()
									})
									if (column.length > 0) {
										item.where = item.where.split('[' + column[0].dataField.toUpperCase() + ']').join('[' + column[0].caption + ']')
									}
								}
								return item
							})
						if (searchItems.length > 0) {
							var self = this
							filterValue.push({
								filter: searchItems.map((i) => i.where).join(' OR '),
								clearFilter: () => {
									self.table['clearFilter']('search')
								},
							})
						}

						// custom filters (popover)
						if (this.customWhereClause && this.customWhereClause.item) {
							var self = this
							var filterItem = {
								filter: this.customWhereClause.item.where,
								clearFilter: () => {
									self.clearCustomFilter()
								},
							}

							if (columns) {
								var column = columns.filter((col) => {
									return col.dataField.toUpperCase() == this.customWhereClause.item.name.toUpperCase()
								})
								if (column.length > 0) {
									filterItem.filter = this.customWhereClause.item.where.split('[' + column[0].dataField.toUpperCase() + ']').join('[' + column[0].caption + ' [CUSTOM_ICON=FILTER]]')
								}
							}

							filterValue.push(filterItem)
						}

						// ids filters
						if (this.$state.params['ids'] && this.$state.params['ids'].length > 0 && this.options.referenceId && this.options.preExecuteClearFilter) {
							if (this.shouldResetContextActionFilter) {
								filterValue = []
								const searchPanel: any = this.table.option('searchPanel')

								if (searchPanel != null) {
									searchPanel.text = ''
								}
							}

							this.shouldResetContextActionFilter = false

							var primaryKeyColumnCaption = 'IDs'
							var primaryColumn = this.localDictionary.getPrimaryKeyColumn(this.options.referenceId)
							primaryKeyColumnCaption = primaryColumn.dataType ? primaryColumn.dataType.toString() : primaryColumn.caption

							var self = this
							filterValue.push({
								filter: '[' + primaryKeyColumnCaption + '] in (' + this.$state.params['ids'].join(', ') + ')',
								clearFilter: () => {
									if (!this.shouldSkipIdFilterReset) {
										self.$state.go('.', { ids: null }, { notify: false }).then(() => {
											if (<any>self.table != null) {
												; (<any>self.table).refresh()
											}
										})
									}
								},
							})
						}

						// additional filters
						if (this.options.additionalFilters && this.options.additionalFilters.length > 0) {
							this.options.additionalFilters.forEach((additionalFilter: any) => {
								if (angular.isString(additionalFilter.columnName) && angular.isString(additionalFilter.condition)) {
									var newFilter = '[' + additionalFilter.columnName + '] ' + additionalFilter.condition
									filterValue.unshift({
										filter: newFilter,
										clearFilter: additionalFilter.clearFilter
											? () => {
												additionalFilter.clearFilter()
											}
											: null,
									})
								} else if (angular.isArray(additionalFilter)) {
									var newFilters = []
									additionalFilter.forEach((af) => {
										var newFilter = '[' + af.columnName + '] ' + af.condition
										newFilters.push(newFilter)
									})
									filterValue.unshift({
										filter: newFilters.join((additionalFilter as any).separator),
										clearFilter: (additionalFilter as any).clearFilter
											? () => {
												; (additionalFilter as any).clearFilter()
											}
											: null,
										catchClearFilter: (additionalFilter as any).catchClearFilter,
									})
								}
							})
						}

						this.filterBox.option('value', filterValue)
						this.$scope.isFilterBoxVisible = filterValue.length > 0
						this.$timeout(() => {
							if (this.table) {
								if (this.$scope.isFilterBoxVisible) {
									table.option('height', 'calc(100% - 40px)')
									if (!this.table['repaintedAfterHeightChange']) {
										this.$timeout(() => {
											this.table.repaint()
											this.table['repaintedAfterHeightChange'] = true
										})
									}
								} else {
									table.option('height', '100%')
									this.table['repaintedAfterHeightChange'] = false
								}
							}
						})
						this.filterBox.repaint()
					}
				}
			}
		})
	}

	protected checkButtonsWidth(width: number) {
		this.$timeout(() => {
			if (width <= 50) {
				this.$scope.isLargeGridHeader = false
				if (this.filterPopover && this.filterPopover.option('visible')) {
					this.filterPopover.hide()
				}
			} else {
				this.$scope.isLargeGridHeader = true
				this.$scope.isFilterListPopupVisible = false
				this.$scope.isFilterFormPopupVisible = false
				if (this.filterFormPopup) this.filterFormPopup.hide()
			}
			angular.element('.dx-datagrid-header-panel > .dx-toolbar').addClass('show-toolbar')
			var actionStatusLargeScreen = true
			if (width <= 400) {
				actionStatusLargeScreen = false
			}
		}, 0)
	}

	protected updateEditedRow(result: EntityModel) {
		var dataSource: DataSource = this.table.option('dataSource')
		var items = dataSource.items()

		if (!result) return false

		for (var i = 0; i < items.length; i++) {
			var item = items[i]

			if (item[CommonHelper.idField] == result.id) {
				result.fields.forEach((f) => {
					var value = f.value
					if (f.value == 'True') value = true
					else if (f.value == 'False') value = false

					var itemKeys = _.keys(item)
					for (var key in itemKeys) {
						// todo: check double ids in columns, edit has diffrent fields
						if (itemKeys[key].indexOf(f.idDataType.toString()) > -1) {
							item[itemKeys[key]] = value
							break
						}
					}
				})

				try {
					dataSource.store().update(i, item)
				} catch (e) { }
			}
		}
	}

	protected updateButtonsHints = _.debounce((isSecondRowVisible, visibleType) => {
		var previewButtonText = ''
		var mapButtonText = ''

		if (visibleType == 'map') {
			previewButtonText = i18n('Grid', 'ShowPreview')
			mapButtonText = i18n('Grid', 'SetMapPosition')
		} else if (visibleType == 'details') {
			mapButtonText = i18n('Grid', 'ShowMap')
			previewButtonText = i18n('Grid', 'SetPreviewPosition')
		}
		if (!isSecondRowVisible) {
			previewButtonText = i18n('Grid', 'ShowPreview')
			mapButtonText = i18n('Grid', 'ShowMap')
		}

		if (this.previewHeaderButtonDeferred)
			this.previewHeaderButtonDeferred.promise.then((button) => {
				button.option('hint', previewButtonText)
			})

		if (this.mapHeaderButtonDeferred)
			this.mapHeaderButtonDeferred.promise.then((button) => {
				button.option('hint', mapButtonText)
			})
	}, 500)

	protected changeColumnFixing(): void {
		const table: any = this.table
		const current: boolean = !!table.option('columnAutoWidth')
		const columns = []

		for (let i = 0; i < table.columnCount(); i++) {
			columns.push(table.columnOption(i))
			columns[i].width = null
		}

		table.option({
			columns: columns,
			columnAutoWidth: !current,
			columnResizingMode: !current ? 'widget' : 'nextColumn',
		})

		const masterDetail = table.option('masterDetail')
		const selection = table.option('selection')

		if (((masterDetail && masterDetail.enabled == true) || (selection && selection.showCheckBoxesMode == 'always')) && current) {
			table.element().find('colgroup').remove()
		}

		//since some version DE doesnt trigger onCustomSave while such column resize option
		const gridState = table.state()
		gridState.columnAutoWidth = !current
		OperatorLayoutSettings.setSetting('SAVED_TABLE_' + this.$scope.options.name, gridState)
		this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.GridOptions, this.$scope.options.name), gridState)
	}

	protected colorFixingButton() {
		var currentFixing = this.table.option('columnAutoWidth')
		this.$timeout(() => {
			if (currentFixing) {
				$('#columnFixingButton' + this.$scope.tableId + '').addClass('active-button')
			} else {
				$('#columnFixingButton' + this.$scope.tableId + '').removeClass('active-button')
			}
		})
	}

	protected hideButtonPopover() {
		if (this.buttonsListPopover) this.buttonsListPopover.option('visible', false)
		this.$scope.isButtonsListPopoverVisible = false
	}

	protected hideAddButtonPopover = () => {
		if (this.addButtonsListPopover) this.addButtonsListPopover.option('visible', false)
		this.$scope.isAddButtonsListPopoverVisible = false
	}

	protected showHideAddButtonPopover = () => {
		this.$scope.isAddButtonsListPopoverVisible = !this.$scope.isAddButtonsListPopoverVisible
		if (this.$scope.isAddButtonsListPopoverVisible) {
			this.addButtonsListPopover.show()
			//this.colorFixingButton()
		} else {
			this.addButtonsListPopover.hide()
		}
	}

	protected showPosisionPopover(target) {
		this.$scope.needPositionPopover = !this.$scope.needPositionPopover
		if (this.$scope.needPositionPopover == false) $('.dx-button').blur()
		this.$timeout(() => {
			this.positionPopoverDeferred.promise.then(() => {
				if (this.$scope.isLargeGridHeader) this.positionPopover.option('position', 'bottom')
				else this.positionPopover.option('position', 'top')
				this.positionPopover.option('visible', true)
				this.positionPopover.option('target', target)
			})
		})
	}

	protected hidePosisionPopover() {
		if (this.positionPopover) this.positionPopover.option('visible', false)
		this.$scope.needPositionPopover = false
	}

	protected tagBoxSelectionChanged(value: any[], previousValue: any[], e = null) {
		if (value.length < previousValue.length && this.canRefreshFilters) {
			var restore = false
			previousValue
				.filter((pv) => value.filter((v) => v.filter == pv.filter || (typeof v.filterName != 'undefined' && typeof pv.filterName != 'undefined' && v.filterName == pv.filterName)).length == 0)
				.forEach((pv) => {
					if (pv.clearFilter) pv.clearFilter()
					else if (pv.catchClearFilter) {
						restore = true
						var index = previousValue.indexOf(pv)
						value.splice(index, 0, pv)
						pv.catchClearFilter()
					} else {
						restore = true
						var index = previousValue.indexOf(pv)
						value.splice(index, 0, pv)
						console.log("Filter don't have clear method!")
					}
				})
			if (restore) {
				this.canRefreshFilters = false
				this.filterBox.option('value', [])
				this.$timeout(() => {
					this.filterBox.option('value', value)
				})
			}
		}
		if (e) {
			this.$scope.refreshFilterBox = true
			this.$timeout(() => {
				var tags = e.element.find('.dx-tag .dx-tag-content span')
				tags.each((index, element) => {
					var tag = angular.element(element)
					var text = tag.text()
					if (text.indexOf('CUSTOM_ICON') > -1) {
						var split = text.split('[CUSTOM_ICON=')
						var icon = split[1].split(']')[0]
						tag.html(text.split('[CUSTOM_ICON=' + icon + ']').join(this.$compile(CommonHelper.getIcon(icon, '#000000', '12'))(this.$scope).html())) //TODO dont have idea how to change to Templatehelper for now
					}
				})
				this.$scope.refreshFilterBox = false
			})
		}
		this.canRefreshFilters = true
	}

	protected clearCustomFilter(ids: any[] = null) {
		const afterClean = () => {
			this.$scope.filterText = null
			this.filteredColumnName = null
			this.$scope.filterTextLabel = i18n('Popup', 'NoColumnSelected')
			this.customWhereClause = null
			if (this.isFilteredLocally) {
				; (<any>this.table).clearFilter('dataSource')
			}
			try {
				; (<any>this.table).refresh()
			} catch (e) {
				this.problemInTable(e)
			}
			if (this.filterList) {
				this.filterList.unselectItem(this.lastSelectedFilterColumnIndex)
			}
			if (this.filterTextArea) {
				this.filterTextArea.option('value', '')
			}
			this.lastSelectedFilterColumnIndex = null
			OperatorLayoutSettings.setSetting(CommonHelper.FILTER_TEXT + '_' + this.options.name, this.$scope.filterText)
			OperatorLayoutSettings.setSetting(CommonHelper.FILTERED_COLUMN + '_' + this.options.name, this.filteredColumnName)
			OperatorLayoutSettings.setSetting(CommonHelper.FILTER_LABEL + '_' + this.options.name, null)
			OperatorLayoutSettings.setSetting(CommonHelper.LAST_SELECTED_ROW + '_' + this.options.name, this.lastSelectedFilterColumnIndex)
			this.setCustomFilterButtonsState()
			this.selectFirstColumnForCustomFilter()
		}
		try {
			if (this.options.preExecuteClearFilter) {
				this.$state.go('.', { ids: ids }, { notify: false }).then(afterClean).catch(afterClean)
			} else {
				afterClean()
			}
		} catch (e) { }
	}

	protected selectFirstColumnForCustomFilter() {
		if (this.filterList) {
			this.lastSelectedFilterColumnIndex = 0
			this.$scope.filterTextLabel = (<any>this.$scope.tableOptions).columns.filter((c) => !c['hideInCustomFilter'] && c.dataType != 'binary' && c.dataType != 'date')[this.lastSelectedFilterColumnIndex].caption
			this.filteredColumnName = (<any>this.$scope.tableOptions).columns.filter((c) => !c['hideInCustomFilter'] && c.dataType != 'binary' && c.dataType != 'date')[this.lastSelectedFilterColumnIndex].dataField
			this.filterList.selectItem(this.lastSelectedFilterColumnIndex)
			this.setCustomFilterButtonsState()
		}
	}

	protected setCustomFilterButtonsState() {
		this.$scope.clearFilterButtonDisabled = true
		this.$scope.submitFilterButtonDisabled = true
		if (this.$scope.filterText || this.filteredColumnName) {
			this.$scope.clearFilterButtonDisabled = false
			if (this.filteredColumnName && this.$scope.filterText) {
				this.$scope.submitFilterButtonDisabled = false
			}
		}
	}

	protected customFilterColumns() {
		if (this.filteredColumnName && this.$scope.filterText) {
			var filteredColumnName = this.filteredColumnName.toUpperCase()

			var columns: DevExpress.ui.dxDataGridColumn[] = this.table.option('columns')
			columns = columns.filter((c) => c.dataField == this.filteredColumnName)
			var column = columns.length > 0 ? columns[0] : null

			if (column) {
				let filters: (string | number)[] = this.$scope.filterText
					.split('\n')
					.filter((f: string) => f != null && f != '' && f.replace(/^\s+|\s+$/g, '') != '')
					.filter((v: string, i: number, a: string[]) => a.indexOf(v) === i)
					.map((item: string) => {
						if (item != null) {
							item = item.trim()
						}

						if (column.dataType == 'number' && !isNaN(+item)) {
							return +item
						} else {
							if (this.isTreeList || this.isFilteredLocally) {
								return item
							} else {
								return "'" + item + "'"
							}
						}
					})

				if (filters.length > 0) {
					if (this.isFilteredLocally) {
						this.customLocalFilterColumns(filters)
					} else {
						var where = '[' + filteredColumnName + ']' + ' in ('
						where += filters.join(',')
						where += ')'
						var item = { name: filteredColumnName, where: where }
						this.customWhereClause = { where, item }
					}
				}
			}
		}
	}

	protected customLocalFilterColumns(filters) {
		if (<any>this.table) {
			var lastCustomFilters = this.getFilters(null, true)
			if (this.isTreeList) {
				lastCustomFilters = lastCustomFilters.filter((filter) => !filter.customFilterId)
			}
			var newCustomFilters: any = []
			filters.forEach((filter) => {
				var filterExpression = [this.filteredColumnName, '=', filter]
				if (newCustomFilters.length > 0) newCustomFilters.push('OR', filterExpression)
				else newCustomFilters.push(filterExpression)
			})
			newCustomFilters.customFilterId = this.filteredColumnName
			newCustomFilters.isCustomFromMenu = true

			if (newCustomFilters.length > 0) {
				if (lastCustomFilters.length > 0) lastCustomFilters.push('and', newCustomFilters)
				else lastCustomFilters.push(newCustomFilters)
			}

			if (lastCustomFilters.length > 0) {
				; (<any>this.table).filter(lastCustomFilters)
				this.treeListCustomFilterColumnName = this.filteredColumnName
			} else (<any>this.table).filter(null)
		}
	}

	protected onRowClick(e) {
		if (e.rowType == 'data') {
			if (this.clickTimer && this.lastRowCLickedId === e.rowIndex) {
				clearTimeout(this.clickTimer)
				this.clickTimer = null
				this.lastRowCLickedId = e.rowIndex
				if (this.options.onRowDoubleClick) this.options.onRowDoubleClick(e)
				if (this.onSelectionChangedTimeout) clearTimeout(this.onSelectionChangedTimeout)
			} else {
				this.clickTimer = setTimeout(() => {
					if (this.options.onRowClick) {
						this.options.onRowClick(e)
					}
					this.clickTimer = null
				}, 250)
			}
			this.lastRowCLickedId = e.rowIndex
		}
	}

	protected onSelectionChanged(e) {
		this.selectedRowCount = _.size(e.selectedRowKeys)
		this.updatePagerInfoText()

		if (this.table) {
			this.$scope.nothingSelected = (<any>this.table).getSelectedRowsData().length == 0
		}

		if (this.onSelectionChangedTimeout) {
			clearTimeout(this.onSelectionChangedTimeout)
		}

		this.onSelectionChangedTimeout = setTimeout(() => {
			if (this.options.onSelectionChanged) {
				this.options.onSelectionChanged(e)
			}
		}, 500)
	}

	//#region Columns

	public static addCustomFiltersToDxColumn(dxColumn, dataType: string, column, addCustomFilters: boolean, localDictionary, referenceTables, self, primaryColumn: OpViewColumn = null) {
		if (!dataType) return

		if (column.type != Enums.DataTypeClass.Binary) {
			const filterOperations: string[] = ['=', '<>']

			const isNonTranslatedDataType: boolean = CommonHelper.nonTranslatedDataTypes.indexOf(column.id) > -1 && !column.skipTranslation

			if (['date', 'datetime', 'number', 'boolean'].indexOf(dataType) < 0 && (!primaryColumn ? true : primaryColumn.idDataType !== column.id)) {
				filterOperations.unshift('contains')
				filterOperations.push('notcontains', 'startswith', 'endswith')
			} else if (['number', 'date', 'datetime'].indexOf(dataType) > -1 || (!primaryColumn ? false : primaryColumn.idDataType === column.id)) {
				filterOperations.push('<', '>', '<=', '>=', 'between')
			}

			if (addCustomFilters && (['date', 'datetime', 'number', 'boolean'].indexOf(dataType) == -1 || (column.id && isNonTranslatedDataType))) {
				if (column.referenceTypeId && localDictionary && column.id && primaryColumn && primaryColumn.idDataType != column.id) {
					localDictionary.getReferenceTable(column.referenceTypeId, column.id).then((table) => {
						referenceTables[column.referenceTypeId] = table.selectBoxData
						if (table && table.selectBoxData && table.selectBoxData.length > 0) dxColumn[this.COLUMN_HAS_CUSTOM_CHOOSE_FILTER] = true
						else {
							if (filterOperations.indexOf('contains') < 0) filterOperations.push('<', '>', '<=', '>=', 'between')
						}
					})
				}

				dxColumn.calculateFilterExpression = (filterValues, selectedFilterOperation, target) => {
					var filterExpression = []
					if (selectedFilterOperation === 'between' && angular.isArray(filterValues)) {
						if (filterValues[0] !== undefined) {
							filterExpression.push([dxColumn.dataField, '>=', filterValues[0]])
						}
						if (filterValues[1] !== undefined) {
							filterExpression.push('and')
							filterExpression.push([dxColumn.dataField, '<=', filterValues[1]])
						}
					} else if (selectedFilterOperation != 'empty' && selectedFilterOperation != 'notempty') {
						filterExpression.push([dxColumn.dataField, selectedFilterOperation || 'contains', filterValues])
					}
					return filterExpression.length > 0 ? filterExpression : null
				}
			} else if ('boolean' == dataType) {
				var clearFilter = () => {
					self.clearColumnFilter(dxColumn.dataField)
				}
				dxColumn.calculateFilterExpression = function (filterValue, selectedFilterOperation, target) {
					if (target === 'filterRow' && filterValue.undefinedBool) {
						const filterExpression = TypeHelper.clone(filterValue.undefinedBool, true)

						filterExpression.isNull = true
						filterExpression.isLocal = true
						filterExpression.nullValue = true
						filterExpression.stack = true
						filterExpression.clearFilter = clearFilter

						return filterExpression
					}

					return this.defaultCalculateFilterExpression(filterValue, selectedFilterOperation)
				}
			} else if ('date' == dataType) {
				var columnType = column.type
				if (!columnType) {
					columnType = dxColumn.columnType
				}
				if (dxColumn.serializationFormatValue) dxColumn.serializationFormat = getSerializationFormat(dxColumn.serializationFormatValue)
				dxColumn.calculateFilterExpression = (filterValue, selectedFilterOperation, target) => {
					var filterExpression = []

					if (filterValue) {
						switch (selectedFilterOperation) {
							case '=':
								var startValue = new Date(filterValue)
								startValue.setSeconds(0)
								startValue.setMilliseconds(0)
								filterExpression.push([dxColumn.dataField, '>=', startValue])

								var endValue = new Date(startValue.toString())
								if (columnType == CommonHelper.datetime) endValue.setMinutes(endValue.getMinutes() + 1)
								else endValue.setHours(endValue.getHours() + 24)
								filterExpression.push('and')
								filterExpression.push([dxColumn.dataField, '<', endValue])
								break
							case '<>':
								var startValue = new Date(filterValue)
								startValue.setSeconds(0)
								startValue.setMilliseconds(0)
								filterExpression.push([dxColumn.dataField, '<', startValue])

								var endValue = new Date(startValue.toString())
								if (columnType == CommonHelper.datetime) endValue.setMinutes(endValue.getMinutes() + 1)
								else endValue.setHours(endValue.getHours() + 24)
								filterExpression.push('or')
								filterExpression.push([dxColumn.dataField, '>=', endValue])
								break
							case 'between':
								if (angular.isArray(filterValue) && filterValue.length == 2 && filterValue[0] != null && filterValue[1] != null) {
									var startValue = new Date(filterValue[0])
									startValue.setSeconds(0)
									startValue.setMilliseconds(0)
									filterExpression.push([dxColumn.dataField, '>=', startValue])

									var endValue = new Date(filterValue[1])
									endValue.setSeconds(0)
									endValue.setMilliseconds(0)
									if (column.type == CommonHelper.datetime) {
										endValue.setMinutes(endValue.getMinutes() + 1)
									}
									//else
									//endValue.setHours(endValue.getHours() + 24);

									filterExpression.push('and')
									filterExpression.push([dxColumn.dataField, '<', endValue])
								}
								break
							case '>':
								var startValue = new Date(filterValue)
								startValue.setSeconds(0)
								startValue.setMilliseconds(0)
								if (columnType == CommonHelper.datetime) startValue.setMinutes(startValue.getMinutes() + 1)
								else startValue.setHours(startValue.getHours() + 24)
								filterExpression.push([dxColumn.dataField, '>=', startValue])
								break
							case '<=':
								var startValue = new Date(filterValue)
								startValue.setSeconds(0)
								startValue.setMilliseconds(0)
								if (columnType == CommonHelper.datetime) startValue.setMinutes(startValue.getMinutes() + 1)
								else startValue.setHours(startValue.getHours() + 24)
								filterExpression.push([dxColumn.dataField, '<', startValue])
								break
							default:
								if (['<', '<=', '>='].indexOf(selectedFilterOperation) > -1) {
									var startValue = new Date(filterValue)
									startValue.setSeconds(0)
									startValue.setMilliseconds(0)
									filterExpression.push([dxColumn.dataField, selectedFilterOperation, startValue])
								}
								break
						}
					}

					return filterExpression.length > 0 ? filterExpression : null
				}
			}
			dxColumn.filterOperations = filterOperations.length > 0 ? filterOperations : undefined
		} else {
			dxColumn.filterOperations = ['empty', 'notempty']
		}
	}

	public static getColumnEditorOptions(accessKey: string, type: number, $timeout: ng.ITimeoutService) {
		var options = {
			accessKey: accessKey,
			dateOutOfRangeMessage: type == CommonHelper.datetime ? i18n('Common', 'ValueIsOutOfRange') : undefined,
			type: type == CommonHelper.datetime ? 'datetime' : type == CommonHelper.date ? 'date' : undefined,
			cancelButtonText: type == CommonHelper.datetime ? i18n('Common', 'Cancel') : undefined,
			applyButtonText: type == CommonHelper.datetime ? i18n('Common', 'Apply') : undefined,

			onInitialized: (e) => {
				if (type == CommonHelper.datetime || type == CommonHelper.date) {
					var startPlaceholder = i18n('Common', 'Start')
					var endPlaceholder = i18n('Common', 'End')
					var lastOnValueChanged = e.component.option('onValueChanged')

					if (e.component._options.placeholder == startPlaceholder) {
						CommonHelper.betweenDateFilterMinVal(e, startPlaceholder, $timeout, this.endDateBoxArray)
						this.startDateBoxArray = [e]
					} else if (e.component._options.placeholder == endPlaceholder) {
						CommonHelper.betweenDateFilterMaxVal(e, endPlaceholder, $timeout, this.startDateBoxArray)
						this.endDateBoxArray = [e]
					}

					e.component.option('onValueChanged', (e) => {
						lastOnValueChanged(e)
						CommonHelper.betweenDateFilterMinVal(e, startPlaceholder, $timeout, this.endDateBoxArray)
						CommonHelper.betweenDateFilterMaxVal(e, endPlaceholder, $timeout, this.startDateBoxArray)
					})
				}
			},
		}

		if (type == CommonHelper.datetime) {
			options['displayFormat'] = ConfigHelper.getDateFormat(ConfigHelper.DateFormat.DATE_TIME_FORMAT_DX)
		} else if (type == CommonHelper.date) {
			options['displayFormat'] = ConfigHelper.getDateFormat(ConfigHelper.DateFormat.DATE_FORMAT_DX)
		}

		return options
	}

	protected getDXColumn(column: ColumnModel, addCustomFilters: boolean, localDictionary, referenceTables, visibleColumns?: ColumnModel[]) {
		let visible = true

		if (_.isArray(visibleColumns)) {
			visible = false
			visibleColumns.forEach((c) => {
				if (c.id == column.id) visible = true
			})
		}

		let dataType = 'string'

		if (column.type === Enums.DataTypeClass.Integer || column.type === Enums.DataTypeClass.Real) {
			dataType = 'number'

			if (!column.skipTranslation) {
				if (column.referenceTypeId && CommonHelper.nonTranslatedDataTypes.indexOf(column.id) === -1) {
					dataType = 'string'
				} else if (column.referenceTypeId && this.primaryColumn && this.primaryColumn.idDataType !== column.id) {
					dataType = 'string'
				}
			}
		} else if (column.type === Enums.DataTypeClass.Boolean) {
			dataType = 'boolean'
		} else if (column.type === Enums.DataTypeClass.Date || column.type === Enums.DataTypeClass.Datetime) {
			dataType = 'date'
			// } else if (column.type === Enums.DataTypeClass.Datetime) {
			// 	dataType = 'datetime'
		} else if (column.type === Enums.DataTypeClass.Binary) {
			dataType = 'binary'
		}

		const dataField = this.getDataField(column)

		if (dataField == null) {
			throw new Error("ColumnModel is missing required values to generate column's data field")
		}

		const caption = column.unitName ? column.caption + ' [' + column.unitName + ']' : column.caption

		const dxColumn: DevExpress.ui.dxDataGridColumn = {
			dataField: dataField,
			dataType: dataType as 'string' | 'number' | 'date' | 'boolean' | 'object' | 'datetime',
			caption: caption,
			allowEditing: column.allowEditing || false,
			visible: visible,
			falseText: i18n('Common', 'False'),
			trueText: i18n('Common', 'True'),
		}

		dxColumn['columnType'] = column.type
		dxColumn['hideInCustomFilter'] = column['hideInCustomFilter']
		dxColumn[GridService.SOURCE_COLUMN_MODEL_FIELD_NAME] = TypeHelper.clone(column, true)
		dxColumn[TableController.COLUMN_HAS_CUSTOM_CHOOSE_FILTER] = false

		if (column.hidden || column.alwaysHidden) {
			dxColumn.visible = false
		}

		if (!column.showInColumnChooser) {
			dxColumn.showInColumnChooser = column.showInColumnChooser
		}

		if (column.cellTemplate) {
			dxColumn.cellTemplate = column.cellTemplate
			dxColumn.allowSearch = false
			dxColumn.allowFiltering = false
		}

		if (column.type == Enums.DataTypeClass.Integer || column.type == Enums.DataTypeClass.Real || column.type == Enums.DataTypeClass.Decimal) {
			dxColumn.customizeText = (data) => {
				return this.dataTypeFormatter.formatDisplayValue(data.value, {
					idDataType: column.id,
					idDataTypeClass: Enums.DataTypeClass.Integer,
				})
			}
		}

		if (column.width != null) {
			dxColumn.width = column.width
		}

		if (column.alignment != null) {
			dxColumn.alignment = column.alignment as 'center' | 'left' | 'right' | undefined
		}

		if (column.calculateCellValue != null) {
			dxColumn.calculateCellValue = column.calculateCellValue
		}

		if (column.groupIndex != null) {
			dxColumn.groupIndex = column.groupIndex
		}
		if (column.allowGrouping != null) {
			dxColumn.allowGrouping = column.allowGrouping
		}

		if (column.validationRules != null) {
			dxColumn.validationRules = column.validationRules
		}

		dxColumn.editorOptions = TableController.getColumnEditorOptions(this.colCount.toString(), column.type, this.$timeout)

		if (column.type == Enums.DataTypeClass.Datetime) {
			const format: string = this.dataTypeFormatter.getDateFormat(ConfigHelper.DateFormat.DATE_TIME_FORMAT_DX, column.id)

			const dateBoxOptions: DevExpress.ui.dxDateBoxOptions = this.dateBoxControlsService.getDateBoxOptions({
				format,
			})

				; (<DevExpress.ui.dxDateBoxOptions>dxColumn.editorOptions).type = dateBoxOptions.type
				; (<DevExpress.ui.dxDateBoxOptions>dxColumn.editorOptions).displayFormat = format
				; (<DevExpress.ui.dxDateBoxOptions>dxColumn.editorOptions).placeholder = format
				; (<DevExpress.ui.dxDateBoxOptions>dxColumn.editorOptions).onOptionChanged = (e) => { }

			dxColumn.customizeText = (options) => {
				if (options.value) {
					return this.dataTypeFormatter.formatDisplayValue(options.value, {
						idDataType: column.id,
						idDataTypeClass: Enums.DataTypeClass.Datetime,
					})
				}

				return TypeHelper.emptyString
			}
		}

		if (column.type == Enums.DataTypeClass.Date) {
			const format: string = this.dataTypeFormatter.getDateFormat(ConfigHelper.DateFormat.DATE_FORMAT_DX, column.id)
				; (dxColumn.editorOptions as DevExpress.ui.dxDateBoxOptions).displayFormat = format
				; (<DevExpress.ui.dxDateBoxOptions>dxColumn.editorOptions).placeholder = format

			dxColumn.customizeText = (options) => {
				if (options.value) {
					return this.dataTypeFormatter.formatDisplayValue(options.value, {
						idDataType: column.id,
						idDataTypeClass: Enums.DataTypeClass.Date,
					})
				}

				return TypeHelper.emptyString
			}
		}

		TableController.addCustomFiltersToDxColumn(dxColumn, dataType, column, addCustomFilters, localDictionary, referenceTables, this, this.primaryColumn)

		return dxColumn
	}

	protected getDxColumns(columns: ColumnModel[], visibleColumns?: ColumnModel[]): DevExpress.ui.dxDataGridColumn[] {
		this.defaultColumns = (<any>Object).assign({}, columns)

		var dxColumns: DevExpress.ui.dxDataGridColumn[] = []

		this.referenceTables = {}

		if (_.isArray(columns)) {
			columns.forEach((column: ColumnModel) => {
				var dxColumn = this.getDXColumn(column, this.options.addCustomFilters, this.localDictionary, this.referenceTables, visibleColumns)

				if (column.editCellTemplate && column.editCellTemplate.isLookup) {
					dxColumn.allowEditing = true
					dxColumn.lookup = column.editCellTemplate.content
				} else if (column.lookup) {
					dxColumn.lookup = column.lookup
				}

				dxColumns.push(dxColumn)
				this.colCount++
			})
		}

		const gridSettings: any = OperatorLayoutSettings.getSetting('SAVED_TABLE_' + this.options.name, null)

		if (gridSettings != null) {
			let savedColumns: any[] = gridSettings.columns

			if (_.isArray(savedColumns)) {
				const hasNoVisibleColumns: boolean = savedColumns.every((column: any) => column.visible === false)

				if (hasNoVisibleColumns) {
					savedColumns = null
				}
			}

			if (_.isArray(savedColumns)) {
				dxColumns.forEach((column: DevExpress.ui.dxDataGridColumn) => {
					savedColumns.forEach((sColumn: DevExpress.ui.dxDataGridColumn) => {
						if (column.dataField == sColumn.dataField) {
							angular.forEach(sColumn, function (value, key) {
								if (key != 'dataType' && key != 'selectedFilterOperation') {
									column[key] = value
								}
							})

							const model: ColumnModel = _.find(columns, (cm: ColumnModel) => column.dataField == cm.name)

							if (model != null && model.alwaysHidden != null) {
								column.visible = !model.alwaysHidden
							}
						}
					})
				})
			} else {
				dxColumns.forEach((column: DevExpress.ui.dxDataGridColumn) => {
					const model: ColumnModel = _.find(columns, (cm: ColumnModel) => column.dataField == cm.name)

					if (model != null && model.alwaysHidden != null) {
						column.visible = !model.alwaysHidden
					}
				})
			}
		}

		return dxColumns
	}

	private clearColumnFilter(id: any, isFromAction = false, operator = null) {
		var upperId: string = id.toString().toUpperCase()
		var table: any = this.table
		if (table) {
			var lastCustomFilters = this.getFilters(upperId.toLowerCase())
			var columns = []
			for (var i = 0; i < table.columnCount(); i++) {
				var column: DevExpress.ui.dxDataGridColumn = table.columnOption(i)
				if (column.dataField && column.dataField.toUpperCase() == upperId) {
					if (column.selectedFilterOperation) {
						if (
							!(isFromAction && operator == null) &&
							((column.selectedFilterOperation == 'between' && operator != 'between') || (column.selectedFilterOperation != 'between' && operator == 'between'))
						) {
							return
						}
						column.selectedFilterOperation = undefined
					}
					column.filterValue = undefined
					column.filterValues = undefined
				}
				columns.push(column)
			}
			table.option('columns', columns)
			// restore custom filters or refresh grid
			if (lastCustomFilters.length > 0) {
				table.filter(lastCustomFilters)
				// if (isFromAction) {
				table.refresh()
				// }
			} else if (!isFromAction) {
				table.filter(null)
				table.refresh()
			} else {
				table.filter(null)
			}
		}
	}

	//#endregion

	protected getWhereClause(filter: any, columnIndex = null): Filter {
		var strings = [],
			items = []
		if (angular.isArray(filter)) {
			var array: any[] = filter

			var isDate = array.length == 3 && Object.prototype.toString.call(array[2]) === '[object Date]'
			var isBool = array.length == 3 && Object.prototype.toString.call(array[2]) === '[object Boolean]'

			if (typeof filter[0] == 'function') {
				filter[0] = (<any>this.$scope.tableOptions).columns[filter[0].columnIndex].caption
			}

			if (
				array.length == 3 &&
				angular.isString(array[0]) &&
				angular.isString(array[1]) &&
				((!array[2] && array['isLocal']) ||
					angular.isString(array[2]) ||
					angular.isArray(array[2]) ||
					angular.isNumber(array[2]) ||
					typeof array[2] == 'boolean' ||
					isDate ||
					(array[1] == 'empty' && !array[2]) ||
					(array[1] == 'notempty' && !array[2]))
			) {
				var columnId: string = array[0]
				var operator: string = array[1]
				var value: string | number | Date = array[2]

				if (this.options.customValueToCaption) {
					var colIdx = this.options.customValueToCaption.map((col) => col.columnName).indexOf(columnId)
					if (colIdx >= 0) {
						var col = this.options.customValueToCaption[colIdx]
						var valIdx = col.values.map((v) => v.value).indexOf(value)
						if (valIdx >= 0) value = col.values[valIdx].caption
					}
				}

				if (isBool) value = value.toString() == 'true' ? 1 : 0

				var column = _.find((<any>this.$scope.tableOptions).columns, (col: any) => {
					return col.dataField == columnId
				})

				// Bo dx-owe filtry zwracają wartość jako string, mimo że kolumna jest liczbowa :(
				if (column && column.dataType == 'number' && _.isString(value) && _.isNumber(Number(value))) {
					const parsedNumber: number = ConvertHelper.localizedStringToNumber(value as string)

					if (_.isFinite(parsedNumber)) {
						value = parsedNumber
					}
				}

				if (column && column.dataType == 'date') {
					if (value && !(filter as any).isEmpty && value != ' ') {
						if (this.changeDateToUTC) {
							var momentDate = moment(value)
							value = momentDate.add(-momentDate.utcOffset(), 'minutes').toDate()
						}

						if (this.needFormatDate) {
							const idDataType: number = OpViewColumn.getColumnIdDataType(column.dataField)

							if (column.columnType == Enums.DataTypeClass.Datetime) {
								const format: string = this.dataTypeFormatter.getDateFormat(ConfigHelper.DateFormat.DATE_TIME_FORMAT_MOMENTJS, idDataType)
								value = moment(value).format(format)

								value = this.dataTypeFormatter.formatDisplayValue(value, {
									idDataType: idDataType,
									idDataTypeClass: Enums.DataTypeClass.Datetime,
								})
							} else {
								const format: string = this.dataTypeFormatter.getDateFormat(ConfigHelper.DateFormat.DATE_FORMAT_MOMENTJS, idDataType)
								value = moment(value).format(format)

								value = this.dataTypeFormatter.formatDisplayValue(value, {
									idDataType: idDataType,
									idDataTypeClass: Enums.DataTypeClass.Date,
								})
							}
						} else {
							value = moment(value).format('YYYY-MM-DD HH:mm') //this is format for WS request
						}
					}
				}

				var isNumber = column && column.dataType == 'number'
				var isDate = column && column.dataType == 'date'
				const isBinary: boolean = column != null && column.columnType == Enums.DataTypeClass.Binary

				columnId = columnId.toUpperCase()

				if (!value && array['isLocal'] && !array['isNull']) {
					value = ''
				}

				var where = '[' + columnId + ']'
				//CONVERT(VARCHAR(25), [columnId], 126)
				if (operator == 'contains') {
					where += " like '%" + value + "%'"
				} else if (operator == 'notcontains') {
					where += " not like '%" + value + "%'"
				} else if (operator == 'startswith') {
					where += " like '" + value + "%'"
				} else if (operator == 'endswith') {
					where += " like '%" + value + "'"
				} else if (operator == 'empty') {
					var prevWhere = where
					where = '(' + prevWhere + ' is null'
					if (!isNumber && !isDate && !isBinary) where += ' or ' + prevWhere + " = ''"
					where += ')'
				} else if (operator == 'notempty') {
					var prevWhere = where
					where = '(' + prevWhere + ' is not null'
					if (!isNumber && !isDate && !isBinary) where += ' and ' + prevWhere + " <> ''"
					where += ')'
				} else if (operator == 'choose') {
					where += " like '" + value + "'"
				} else if (operator == 'between') {
					if (!angular.isNumber(value[0])) value[0] = 0
					if (!value[1] || !angular.isNumber(value[1])) where += ' >= ' + value[0]
					else where = where + ' >= ' + value[0] + ' and ' + where + ' <= ' + value[1]
				} else if (array['isLocal'] && array['empty']) {
					if (value === TypeHelper.emptyString) {
						where = where + " = ''"
					} else if (value === null) {
						where = where + ' is null'
					}
				} else if (array['isLocal'] && array['notempty']) {
					if (value === TypeHelper.emptyString) {
						where = where + " <> ''"
					} else if (value === null) {
						where = where + ' is not null'
					}
				} else {
					if (angular.isNumber(value)) where += ' ' + operator + ' ' + value
					else {
						if (value == null) {
							if (array['nullValue']) {
								where += ' is null'
							} else {
								where += ' ' + operator + ' null'
							}
						} else {
							where += ' ' + operator + " '" + value + "'"
						}
					}
				}

				where = where.replace(new RegExp('\u200B', 'g'), '')
				strings.push(where)

				if (columnIndex) {
					var c = (<any>this.table).columnOption(columnIndex)
					operator = c.selectedFilterOperation
				}

				var clearFilter = (filter as any).clearFilter
				if (!clearFilter) {
					clearFilter = () => {
						this.clearColumnFilter(columnId, false, operator)
					}
				}
				items.push({ name: columnId, where: where, clearFilter: clearFilter, filterName: (filter as any).filterName })
			} else {
				var innerStrings = []
				// fix for filter date
				if (
					(filter as any).columnIndex != null &&
					typeof (filter as any).columnIndex != 'undefined' &&
					angular.isArray(filter[0]) &&
					angular.isArray(filter[2]) &&
					((angular.isDate(filter[0][2]) && angular.isDate(filter[2][2])) || (moment(filter[0][2]).isValid() && moment(filter[2][2]).isValid()))
				) {
					var columnOption = (<any>this.table).columnOption((filter as any).columnIndex)
					if (columnOption) {
						var fromWhere = this.getWhereClause(filter[0], (filter as any).columnIndex)
						var toWhere = this.getWhereClause(filter[2])
						var where = fromWhere.where + ' ' + filter[1].toUpperCase() + ' ' + toWhere.where
						return {
							where: where,
							items: [
								{
									name: fromWhere.items[0].name,
									where: where,
									clearFilter: fromWhere.items[0].clearFilter,
									filterName: fromWhere.items[0].filterName,
								},
							],
						}
					}
				}
				// search filter because filter don't have customId or customFilterId
				const searchPanel: any = this.table.option('searchPanel')
				const searchText: string = searchPanel != null ? searchPanel.text : null
				var isSearch = !(filter as any).customId && !(filter as any).customFilterId && !(filter as any).getSearch && !TypeHelper.isNullOrEmpty(searchText)
				// custom filters (popover)
				var isCustomFilter = !!(filter as any).customFilterId && !filter['custom']

				if (isCustomFilter) {
					filter['custom'] = true
					var customFilters = this.getWhereClause(filter)
					var self = this
					return {
						where: customFilters.where,
						items: [
							{
								name: customFilters.items[0].name,
								where: customFilters.where,
								clearFilter: () => {
									var filters = self.getFilters(null)
									if (self.table) {
										if (filters.length > 0) {
											self.table['filter'](filters)
										} else {
											self.table['filter'](null)
										}
									}
									self.clearCustomFilter()
								},
								isCustom: true,
								filterName: customFilters.items[0].filterName,
							},
						],
					}
				}

				if ((filter as any).stack && !filter['isStack'] && (filter as any).clearFilter) {
					filter['isStack'] = true
					var stackFilters = this.getWhereClause(filter)
					return {
						where: stackFilters.where,
						items: [
							{
								name: stackFilters.items.map((i) => {
									return { name: i.name, where: i.where }
								}),
								where: stackFilters.where,
								clearFilter: (filter as any).clearFilter,
								filterName: (filter as any).filterName,
							},
						],
					}
				}

				array.forEach((value, index) => {
					var innerWhere = this.getWhereClause(value)
					if (innerWhere) {
						if (innerWhere.where != '') innerStrings.push(innerWhere.where)
						if (innerWhere.items.length > 0) {
							if (isSearch)
								innerWhere.items = innerWhere.items.map((i) => {
									i['isSearch'] = true
									return i
								})
							items.push(...innerWhere.items)
						}
					}
				})
				if (innerStrings.length > 0) {
					if ((filter as any).stack) strings.push(...innerStrings)
					else strings.push('(', ...innerStrings, ')')
				}
			}
		} else if (angular.isString(filter)) {
			strings.push(filter)
		}
		return {
			where: strings.join(' '),
			items: items,
		}
	}

	protected createDateFilter(textArray) {
		var startDate
		var endDate
		if (textArray[2]) {
			if (textArray[2].length == 2) {
				if (textArray[3]) {
					if (textArray[3].length == 1) {
						var patt = /[12]/g
						if (textArray[3] == '0') {
							startDate = textArray[0] + '1 00:00:00'
							endDate = textArray[0] + '9 23:59:59'
						} else if (patt.test(textArray[2])) {
							startDate = textArray[0] + '0 00:00:00'
							endDate = textArray[0] + '9 23:59:59'
						} else {
							startDate = textArray[0] + '0 00:00:00'
							endDate = textArray[0] + '1 23:59:59'
						}
					} else {
						startDate = textArray[0]
					}
				} else {
					startDate = textArray[0] + '-01 00:00:00'
					var numberOfDays = moment(textArray[0]).daysInMonth()
					endDate = textArray[0] + '-' + numberOfDays.toString() + ' 23:59:59'
				}
			} else {
				if (textArray[2] == '0') {
					startDate = textArray[0] + '1-01 00:00:00'
					endDate = textArray[0] + '9-30 23:59:59'
				} else {
					startDate = textArray[0] + '0-01 00:00:00'
					endDate = textArray[0] + '2-31 23:59:59'
				}
			}
		} else {
			startDate = textArray[1] + '-01-01 00:00:00'
			endDate = textArray[1] + '-12-31 23:59:59'
		}
		return { startDate: startDate, endDate: endDate }
	}

	private onValueChangedInCustomChoose(customChooseFilters, column, values: any[], tagBox = null) {
		var filters = this.getFilters(column.dataField)

		if (values) {
			customChooseFilters[column.dataField] = []
			var chooseFilters: any = []
			values.forEach((val, index) => {
				var v = val
				customChooseFilters[column.dataField].push(val)
				var chooseFilter: any = [column.dataField, '=', val.caption]
				chooseFilter.clearFilter = () => {
					var index = customChooseFilters[column.dataField].indexOf(v)
					customChooseFilters[column.dataField].splice(index, 1)
					if (this.customChooseLastTarget && tagBox && '#filterButton' + column.dataField == this.customChooseLastTarget) tagBox.option('value', customChooseFilters[column.dataField])
					else {
						this.onValueChangedInCustomChoose(customChooseFilters, column, customChooseFilters[column.dataField])
					}
				}
				if (chooseFilters.length > 0) chooseFilters.push('or', chooseFilter)
				else chooseFilters.push(chooseFilter)
			})

			chooseFilters.customId = column.dataField

			if (chooseFilters.length > 0) {
				if (filters.length > 0 && values.length > 0) filters.push('and', chooseFilters)
				else filters.push(chooseFilters)
			}
		}
		try {
			this.canRefreshFilters = false
			if (filters.length == 0) (<any>this.table).filter(null)
			else (<any>this.table).filter(filters)
		} catch (e) {
			this.problemInTable(e)
		}
	}

	protected hackForCustomFilters(e) {
		// hack for custom filters
		if (!this.hackForCustomFiltersInit) {
			var lastGetFilterOperationMenuItems: () => any[] = e.component._views.columnHeadersView._getFilterOperationMenuItems
			e.component._views.columnHeadersView._getFilterOperationMenuItems = (column) => {
				const filters: any[] = lastGetFilterOperationMenuItems.call(e.component._views.columnHeadersView, column)
				const resetFilter = filters.pop()

				filters.push(
					{
						icon: 'close',
						name: 'empty',
						selected: false,
						text: i18n('Common', 'Empty'),
					},
					{
						icon: 'comment',
						name: 'notempty',
						selected: false,
						text: i18n('Common', 'NotEmpty'),
					}
				)

				if (column[TableController.COLUMN_HAS_CUSTOM_CHOOSE_FILTER] === true) {
					filters.push({
						icon: 'check',
						name: 'choose',
						selected: false,
						text: i18n('Common', 'Select'),
					})
				}

				filters.push(resetFilter)

				return filters
			}

			var customChooseFilters = {}
			var customFilters = {}

			var lastUpdateFilterOperationChooser: () => any[] = e.component._views.columnHeadersView._updateFilterOperationChooser

			e.component._views.columnHeadersView._updateFilterOperationChooser = ($menu, column, $editorContainer) => {
				lastUpdateFilterOperationChooser.call(e.component._views.columnHeadersView, $menu, column, $editorContainer)

				var menu = $menu.dxMenu('instance')
				var onClick = menu._actions.onItemClick
				menu._actions.onItemClick = (properties) => {
					if (!properties.itemData.name) {
						this.clearColumnFilter(column.dataField, true)
					} else {
						if (column.filterValue && (properties.itemData.name == 'empty' || properties.itemData.name == 'notempty' || properties.itemData.name == 'choose')) {
							let columns: DevExpress.ui.dxDataGridColumn[] = e.component.option('columns')
							columns.forEach((c) => {
								if (c.dataField == column.dataField) column.filterValue = null
							})
							e.component.option('columns', columns)
							this.$timeout(() => {
								onClick.call(menu, properties)
							})
						} else {
							onClick.call(menu, properties)
						}
					}
				}

				var dxIcons: JQuery = $menu.find('.dx-item-content')
				var input: JQuery = $editorContainer.find('.dx-texteditor-input')

				var isCustom = false
				dxIcons.each((i, dxIcon) => {
					if (column.selectedFilterOperation == 'empty') {
						$(dxIcon).empty().append('<i class="dx-icon dx-icon-close"></i>')
						isCustom = true
					} else if (column.selectedFilterOperation == 'notempty') {
						$(dxIcon).empty().append('<i class="dx-icon dx-icon-comment"></i>')
						isCustom = true
					} else if (column.selectedFilterOperation == 'choose') {
						$(dxIcon).empty().append('<i class="dx-icon dx-icon-check"></i>')
						isCustom = true
					}
				})

				var filters = this.getFilters(column.dataField && column.dataField.toLowerCase ? column.dataField.toLowerCase() : null)
				if (customFilters[column.dataField] && !isCustom) {
					this.canRefreshFilters = false
				} else if (customFilters[column.dataField] != 'choose' && column.selectedFilterOperation == 'choose') {
					this.canRefreshFilters = false
				}

				var isChoose = false
				if (customFilters[column.dataField] == 'choose' && column.selectedFilterOperation != 'choose') {
					customChooseFilters[column.dataField] = []
					this.$scope.needChooseFilterPopover = false
					this.chooseFilterPopoverDeferred = this.$q.defer<DevExpress.ui.dxPopover>()
					this.chooseFilterTagBoxDeferred = this.$q.defer<DevExpress.ui.dxTagBox>()
				} else if (customFilters[column.dataField] == 'choose' && column.selectedFilterOperation == 'choose') {
					isChoose = true
				}

				customFilters[column.dataField] = isCustom ? column.selectedFilterOperation : false

				var containerCopy = null
				if (isCustom) {
					if (column.selectedFilterOperation == 'choose') {
						var div = '<div id="filterButton' + column.dataField + '" class="filter-button"></div>'
						$editorContainer.append(div)
						$editorContainer.children('.filter-button').dxButton(<DevExpress.ui.dxButtonOptions>{
							text: i18n('Common', 'SelectPlaceholder'),
							onClick: (event) => {
								this.$scope.needChooseFilterPopover = true
								this.$timeout(() => {
									this.chooseFilterPopoverDeferred.promise.then((popover) => {
										var target = '#filterButton' + column.dataField
										this.customChooseLastTarget = target
										popover.option('target', this.customChooseLastTarget)
										popover.toggle(true)

										if (!customChooseFilters[column.dataField]) customChooseFilters[column.dataField] = []

										this.chooseFilterTagBoxDeferred.promise.then((tagBox) => {
											tagBox.option('onValueChanged', null)
											tagBox.option({
												dataSource:
													this.referenceTables[
													_.find(this.defaultColumns, (col) => {
														if (col.name.toLocaleLowerCase() == column.dataField) return true
														return false
													}).referenceTypeId
													],
												value: customChooseFilters[column.dataField],
												onValueChanged: (e) => {
													this.onValueChangedInCustomChoose(customChooseFilters, column, e.value, tagBox)
												},
											})
										})
									})
								})
							},
						})
						if (column.filterValue) {
							column.filterValue = null
						}
					} else if (column.selectedFilterOperation) {
						$editorContainer.children('.filter-button').remove()

						let filterOperation = column.selectedFilterOperation
						let filter: any = [column.dataField, filterOperation, ' ']
						const isLocal = !this.options.request && !this.options.getDataSourceMethod

						if (isLocal) {
							var separatorFilter = 'OR'
							if (column.selectedFilterOperation == 'empty') {
								filterOperation = '='
							} else if (column.selectedFilterOperation == 'notempty') {
								separatorFilter = 'AND'
								filterOperation = '<>'
							}

							var nullFilter: any = [column.dataField, filterOperation, null]
							nullFilter.isLocal = true
							nullFilter.isNull = true
							nullFilter.isEmpty = true

							if (column.selectedFilterOperation === 'empty') {
								nullFilter.empty = true
							} else if (column.selectedFilterOperation === 'notempty') {
								nullFilter.notempty = true
							}

							let nullStringFilter: any = null

							if (column.dataType === 'string') {
								nullStringFilter = [column.dataField, filterOperation, '']
								nullStringFilter.isLocal = true
								nullStringFilter.isEmpty = true

								if (column.selectedFilterOperation === 'empty') {
									nullStringFilter.empty = true
								} else if (column.selectedFilterOperation === 'notempty') {
									nullStringFilter.notempty = true
								}
							}

							if (nullStringFilter) {
								filter = [nullFilter, separatorFilter, nullStringFilter]
							} else {
								filter = nullFilter
							}

							filter.stack = true
							filter.clearFilter = () => {
								this.clearColumnFilter(column.dataField)
							}
						}

						filter.customId = column.dataField
						filter.isLocal = isLocal
						if (filters.length > 0) filters.push('and', filter)
						else filters.push(filter)

						input.css('opacity', 0)
						input.prop('readonly', 'readonly')
					}
				} else {
					$editorContainer.children('.filter-button').remove()
					input.css('opacity', 1)
					input.prop('readonly', false)
				}

				try {
					if (e.component && e.component.getDataSource() && !isChoose) {
						if (filters.length == 0) e.component.filter(null)
						else {
							e.component.filter(filters)
						}
					}
				} catch (error) {
					console.log(e)
					this.problemInTable(error)
				}
			}

			this.hackForCustomFiltersInit = true
		}
	}

	// get all custom filters without one
	private getFilters(id: any, isCustomFromMenu = false) {
		try {
			if (!this.table || !this.table.option('dataSource') || !(<any>this.table).filter) return []
			var newFilters = []

			var lastF: any = (<any>this.table).filter()
			if (lastF) {
				lastF.forEach((lf) => {
					if (!lf.customFilterId && lf != 'and' && lf != 'or' && (!lf.customId || (lf.customId && lf.customId.toString().toLowerCase() != id) || id == null)) {
						if (newFilters.length > 0) newFilters.push('and', lf)
						else newFilters.push(lf)
					} else if (this.treeListCustomFilterColumnName === lf.customFilterId && this.isTreeList) {
						if (newFilters.length > 0) newFilters.push('and', lf)
						else newFilters.push(lf)
					} else if (!isCustomFromMenu && lf.isCustomFromMenu) {
						if (newFilters.length > 0) newFilters.push('and', lf)
						else newFilters.push(lf)
					}
				})
			}
		} catch (e) {
			this.problemInTable(e)
		}
		return newFilters
	}

	/**
	 * Returns the name of a property that should serve as a data field in a DX column
	 */
	protected getDataField(column: ColumnModel): string {
		if ('idName' in column && column.idName.length > 0) {
			return column.idName.charAt(0).toLowerCase() + column.idName.slice(1)
		}

		if ('name' in column) {
			return column.name.toLowerCase()
		}

		if ('id' in column) {
			return (column as any).id.toString()
		}

		return null
	}

	public setColumnChooserTooltip(): void {
		if (!this.isColumnChooserTooltipInitialized) {
			const columnChooserElement: JQuery = this.findColumnChooserElement()
			if (columnChooserElement == null) {
				console.warn(this.LOG_STRING, 'ColumnChooserElement not found.')
				return
			}
			columnChooserElement.mouseover((e) => {
				if (columnChooserElement != null) {
					const columnChooserWidth: number = columnChooserElement.find('.dx-item.dx-treeview-item').width()
					const elementWidth: number = $(e.target).find('span').width()
					if (elementWidth != null && columnChooserWidth != null && elementWidth > columnChooserWidth) {
						this.$scope.columnChooserTooltipText = e.target.textContent
						this.columnChooserTooltip.show(e.target)
					} else {
						this.columnChooserTooltip.hide()
					}
				}
			})
			this.isColumnChooserTooltipInitialized = true
		}
	}

	// DX Internals

	protected findColumnChooserElement(): JQuery {
		if (
			this.table != null &&
			this.table['_views'] != null &&
			this.table['_views'].columnChooserView != null &&
			this.table['_views'].columnChooserView._columnChooserList != null &&
			this.table['_views'].columnChooserView._columnChooserList._$element != null
		) {
			return $(this.table['_views'].columnChooserView._columnChooserList._$element)
		}
	}

	//DX Internals end

	//#region Foratting

	protected getDataTypeFormatter(): JQueryPromise<DataTypeFormatter> {
		let columnModels: ColumnModel[] = []

		if (this.$scope.options.defaultColumnsDictionary != null && this.$scope.options.columnsDictionary != null) {
			columnModels = [...this.$scope.options.columnsDictionary, ...this.$scope.options.defaultColumnsDictionary]
		} else if (this.$scope.options.columnsDictionary != null) {
			columnModels = [...this.$scope.options.columnsDictionary]
		}

		const columnIdDataTypes: Enums.DataType[] = _.map(columnModels, (columnModel: ColumnModel) => {
			return columnModel.id
		})

		return this.dataTypeFormatService.getDataTypeFormatter(columnIdDataTypes).fail((error: any) => {
			console.warn(this.LOG_STRING, 'Unable to load data type formatter, proceeding without it.', error)
		})
	}

	//#endregion

	//#region Filters

	/**
	 * A data field name of a column which filter row editor has been recently focused.
	 */
	private lastFocusedFilterEditorColumnField: string

	/**
	 * Sets focus events on the given column filter editor.
	 *
	 * Warning. This method mutates the editor's options.
	 *
	 * @param e DevExpress onEditorPreparing event.
	 */
	protected setRestoreEditorFocus(e: any): void {
		if (e.parentType == 'filterRow') {
			if (e.editorOptions == null) {
				e.editorOptions = {}
			}

			if (this.lastFocusedFilterEditorColumnField != null && this.lastFocusedFilterEditorColumnField === e.dataField) {
				e.editorOptions.onInitialized = (e: any) => {
					if (e.component.focus != null) {
						this.$timeout(() => {
							e.component.focus()
						}, 200)
					}
				}
			}

			e.editorOptions.onFocusIn = () => {
				this.lastFocusedFilterEditorColumnField = e.dataField
			}

			e.editorOptions.onFocusOut = () => {
				this.lastFocusedFilterEditorColumnField = null
			}
		}
	}

	//#endregion

	//#region Pager Info Text

	protected isShortPagerInfoText: boolean = true

	protected getPagerInfoTextFunc = (...args: number[]) => {
		return this.prepratePagerInfoText(this.getPagerInfoText())
	}

	protected updatePagerInfoText(): void {
		if (this.table != null) {
			const lightInfoElement: JQuery = this.getPagerInfoTextLightElement()

			if (lightInfoElement != null && lightInfoElement.length > 0) {
				lightInfoElement.text(i18n('Common', 'Of'))
			} else {
				const infoElement: JQuery = this.getPagerInfoTextElement()
				const pagerInfoText: string = this.prepratePagerInfoText(this.getPagerInfoText())

				if (infoElement != null) {
					infoElement.text(pagerInfoText)
				}
			}
		}
	}

	private getPagerInfoTextElement(): JQuery {
		return this.tableElement.find('.dx-pages .dx-info')
	}

	private getPagerInfoTextLightElement(): JQuery {
		return this.tableElement.find('.dx-light-pages .dx-info-text')
	}

	/**
	 * Prepends the pager info text with invisible black characters, to create enought width, so the pager text dissapears instead of being shiftet down.
	 */
	private prepratePagerInfoText(text: string): string {
		const estimatedTextLength: number = this.getEstimatedPagerInfoTextLength()

		return TypeHelper.padStart(text, estimatedTextLength, TypeHelper.ZERO_WIDTH_SPACE)
	}

	private getEstimatedPagerInfoTextLength(): number {
		return this.getTranslatedPagerInfoText(999999, 999999, 999999, 9999).length
	}

	private getPagerInfoText(): string {
		const currentPage: number = (<any>this.table).pageIndex() + 1
		const totalPages: number = (<any>this.table).pageCount()
		const totalItems: number = (<any>this.table).totalCount()
		const selectedCount: number = this.getSelectedRowsCount()

		return this.getTranslatedPagerInfoText(currentPage, totalPages, totalItems, selectedCount)
	}

	private getTranslatedPagerInfoText(currentPage: number, totalPages: number, totalItems: number, selectedCount: number = null): string {
		const infoText: string = this.isShortPagerInfoText ? i18n('Grid', 'PagerTextShort') : i18n('Grid', 'PagerText')
		const selectedCountText: string = this.getTranslatedPagerSelectedInfoText(selectedCount)

		return infoText.replace('{0}', currentPage.toString()).replace('{1}', totalPages.toString()).replace('{2}', totalItems.toString()).replace('.', selectedCountText)
	}

	private getTranslatedPagerSelectedInfoText(selectedCount: number): string {
		if (selectedCount != null && selectedCount > 0) {
			return selectedCount + ' ' + i18n('Common', 'selected') + ' / '
		}

		return ''
	}

	//#endregion

	//#region Selection

	private selectedRowCount: number = 0

	protected getSelectedRowsCount(): number {
		return this.selectedRowCount || 0
	}

	//#endregion
}

var getSerializationFormat = function (value) {
	var format = getIso8601Format(value)
	if (format) {
		return format
	} else {
		if (value.indexOf(':') >= 0) {
			return 'yyyy/MM/dd HH:mm:ss'
		} else {
			return 'yyyy/MM/dd'
		}
	}
}

var getIso8601Format = function (text) {
	var ISO8601_PATTERN = /^(\d{4,})(-)?(\d{2})(-)?(\d{2})(?:T(\d{2})(:)?(\d{2})?(:)?(\d{2}(?:\.(\d+))?)?)?(Z|([\+\-])(\d{2})(:)?(\d{2})?)?$/
	var ISO8601_TIME_PATTERN = /^(\d{2}):(\d{2})(:(\d{2}))?$/
	var ISO8601_PATTERN_PARTS = ['', 'yyyy', '', 'MM', '', 'dd', 'THH', '', 'mm', '', 'ss', '.SSS']

	var parts = text.match(ISO8601_PATTERN),
		result = ''
	if (!parts) {
		parts = text.match(ISO8601_TIME_PATTERN)
		if (parts) {
			return parts[3] ? 'HH:mm:ss' : 'HH:mm'
		}
		return
	}
	for (var i = 1; i < ISO8601_PATTERN_PARTS.length; i++) {
		if (parts[i]) {
			result += ISO8601_PATTERN_PARTS[i] || parts[i]
		}
	}
	if ('Z' === parts[12]) {
		result += "'Z'"
	}
	if (parts[14]) {
		if (parts[15]) {
			result += 'xxx'
		} else {
			if (parts[16]) {
				result += 'xx'
			} else {
				result += 'x'
			}
		}
	}
	return result
}
